/**
 * Room 3D Viewer Component (Modular)
 *
 * Interactive 3D room visualization using Three.js.
 * Features board-level product rendering, camera-driven transparency,
 * and color-accurate materials.
 */

import { useTranslation } from 'react-i18next';
import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import * as THREE from 'three';
import { RotateCcw, Maximize, Minimize, DoorOpen, DoorClosed, Frame } from 'lucide-react';

import { Room3DViewerProps, WallFace, SurfaceFace, GarageConfigProp, ContextRoom, RoomBoardLayouts, CeilingPlaneLayout, isCeilingFace, ceilingHeightAt, ceilingNormalAt, ceilingPeak } from './types';
import { FloorSegment, WALL_FACES, contextFootprint, contextRoomAt, contextWallSegments, wallHidesFocusRoom } from './contextRooms';
import { createGarageDoorGroup } from './garageDoorBuilder';
import { buildRoomFraming, disposeFramingGroup, framingShape } from './framingBuilder';
import { buildYard } from './yardBuilder';
import { yardInRoom } from '../../../calculations/plan/yard';
import { placeMovingPart } from './openingMeshBuilder';
import { addStagedProps, standInsForExport } from './stagingMeshes';
import { placeAccessory } from './accessoryMeshes';
import { SLATWALL_PROFILE } from './profiles/slatwall';
import { lampPlacements } from './lampLayout';
import { getRenderSettings } from './renderSettings';
import { RenderManifest, cameraManifest, exportRoomGlb } from './sceneExport';

/** How far a SlatWall accessory's back sits out from the wall plane: against the face of the slats. */
const SLAT_FACE_Z = SLATWALL_PROFILE.depthSize;
import { getFramingSettings, onFramingSettingsChange } from './framingSettings';

// Module-level scratch vector reused every frame to avoid GC churn.
const _tmpViewDir = new THREE.Vector3();
import { createScene, addLighting, addInteriorLight, addLightProbe, ENV_IMAGES, createCamera, configureRenderer, addBorders } from './sceneBuilder';
import {
  createLegacyWallMaterial,
  createLegacyCeilingMaterial,
  createFloorMaterial,
} from './materialFactory';
import {
  INITIAL_ROTATION,
  DEFAULT_FOV,
  applyDragRotation,
  applyZoom,
  interpolateState,
  calculateZoomToFit,
  easeInOutCubic,
  shortestAngleDelta,
} from './cameraController';
import { attachTouchControls } from './touchController';
import { exitFullscreenSafe, fullscreenSupported, requestFullscreenSafe } from '../../../utils/fullscreen';
import { addDimensionLines, formatDimension } from './dimensionLabels';
import { generateFullRoomLayouts } from './boardLayoutEngine';
import { buildWallMesh, buildCeilingMesh, buildCeilingPlanesMesh, bakeRoomClipping } from './wallMeshBuilder';
import { setBareStudsVisible } from './bareFill';
import { outlineTopAt } from '../../../calculations/sections/outline';
import {
  calculateWallOpacities,
  lerpOpacities,
  applyOpacityToGroup,
  TransparencyState,
} from './transparencyController';

import { registerLight, unregisterLight, LightingRegistryEntry } from '../../dev/lightingRegistry';
import {
  registerVisualizer,
  unregisterVisualizer,
  generateVisualizerId,
  VisualizerDebugEntry,
} from './visualizerDebug';
import { DESIGNER_STRINGS as S } from '../../designer/strings';
import { RenderPipeline } from './renderPipeline';
import { onRenderSettingsChange } from './renderSettings';
import { getLensPreview, onLensChange, reportViewLens, verticalFovForMm } from './lensPreview';
import { towardWall } from './panorama';
import { CameraPose, cornerFor, cornerPose } from './renderPose';
import '../Room3DViewer.css';

const FADING_SURFACES = ['front', 'back', 'left', 'right', 'ceiling'] as const;
/** Where the eye sits inside the room, relative to the room's centre (feet). */
const INSIDE_EYE_OFFSET = new THREE.Vector3(0.0137, 0, 0.0171);
/** The field of view inside a room (the orbit view's is DEFAULT_FOV). */
const INSIDE_FOV = 75;

/** Where the user's Show / Hide Framing choice is kept. */
const FRAMING_PREFERENCE_KEY = 'designer.showFraming';
function readFramingPreference(): boolean | null {
  try {
    const stored = window.localStorage.getItem(FRAMING_PREFERENCE_KEY);
    return stored === null ? null : stored === 'true';
  } catch { return null; }
}
function writeFramingPreference(show: boolean): void {
  try { window.localStorage.setItem(FRAMING_PREFERENCE_KEY, String(show)); } catch { /* private mode: the choice lasts the session */ }
}
/** How long a door takes to swing open or shut, ms. */
const OPENER_MS = 900;
/** An average person's eye height, 5' 10". A room lower than that puts the eye half way up it. */
const HUMAN_EYE_FT = 70 / 12;
export const insideEyeHeight = (roomHeight: number): number => (roomHeight >= HUMAN_EYE_FT ? HUMAN_EYE_FT : roomHeight / 2);
/**
 * Compile the scene's shaders ahead of its first frame, resolving once every
 * program is ready. With KHR_parallel_shader_compile the driver compiles them
 * off the main thread and this polls; without it, `compile` is the compile
 * and the poll is a formality. three's own `compileAsync` is this, but it
 * throws when a material it is polling is disposed meanwhile (the pipeline
 * replaces the ground on attach, a rebuild can follow a rebuild): here such a
 * material simply drops out of the wait. Also stops waiting when `cancelled`.
 */
function compileScene(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, cancelled: () => boolean): Promise<void> {
  type Program = { isReady(): boolean };
  let materials: Set<THREE.Material>;
  try {
    materials = (renderer.compile(scene, camera) as unknown as Set<THREE.Material> | undefined) ?? new Set();
  } catch {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    const check = () => {
      if (cancelled()) { resolve(); return; }
      materials.forEach((material) => {
        const program = (renderer.properties.get(material) as { currentProgram?: Program }).currentProgram;
        if (!program || program.isReady()) materials.delete(material);
      });
      if (materials.size === 0) resolve();
      else setTimeout(check, 10);
    };
    check();
  });
}

/** Everything under `root` that holds GPU memory (geometries, materials), for freeing later. */
/**
 * Bring back whatever the view has faded out or hidden of a room — the walls
 * between the camera and the room when it is orbiting outside, a wall faded
 * for a locked one — for a picture of the whole room (a .glb export, a
 * still). Returns the function that puts it all back as it was.
 */
function showWholeRoom(roomGroup: THREE.Group): () => void {
  const shown: { object: THREE.Object3D; visible: boolean }[] = [];
  const forced: { material: THREE.Material; opacity: number; depthWrite: boolean }[] = [];
  for (const child of roomGroup.children) {
    if (!child.userData.wallFace) continue;
    shown.push({ object: child, visible: child.visible });
    child.visible = true;
    child.traverse((part) => {
      const material = (part as THREE.Mesh).material as THREE.Material | undefined;
      if (!material || Array.isArray(material) || material.userData.kind === 'seam') return;
      const full = typeof material.userData.baseOpacity === 'number' ? material.userData.baseOpacity : 1;
      if (material.opacity === full) return;
      forced.push({ material, opacity: material.opacity, depthWrite: material.depthWrite });
      material.opacity = full;
      material.depthWrite = full >= 1;
    });
  }
  return () => {
    for (const f of forced) { f.material.opacity = f.opacity; f.material.depthWrite = f.depthWrite; }
    for (const s of shown) s.object.visible = s.visible;
  };
}

function disposablesOf(root: THREE.Object3D, into: { dispose(): void }[]): void {
  root.traverse((child) => {
    if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
      // A prop's photoreal preview shares its geometry with the parsed file
      // (stagingMeshes.ts): freed here, the next rebuild would upload its
      // 100k triangles again. Its materials are its own and go.
      if (child.geometry && !child.userData.propPreviewMesh) into.push(child.geometry);
      const material = child.material as THREE.Material | THREE.Material[] | undefined;
      for (const m of Array.isArray(material) ? material : [material]) if (m) into.push(m);
    }
  });
}

/** Ctrl+wheel inside the room: feet of eye height per wheel unit (a notch is ~100 units → 6"). */
const EYE_LIFT_PER_WHEEL_UNIT = 0.005;
/** How close to a wall (feet) a double-click on the floor may take the eye. */
const INSIDE_WALL_CLEARANCE_FT = 1;
/** Gliding to a double-clicked spot on the floor (ms). */
const INSIDE_GLIDE_MS = 700;
/** Floor plans: the camera's journey to a newly focused room (ms). */
const FOCUS_TRAVEL_MS = 900;
/** Floor plans: a wall's thickness (a stud's depth) — floors run half of it under each wall, to meet. */
const PLAN_WALL_GAP_FT = 3.5 / 12;
/** A click on another room waits this long, in case it is the first half of a double-click (ms). */
const FOCUS_CLICK_DELAY_MS = 240;

/** One of the plan's other rooms, as built into the scene. */
interface BuiltContextRoom {
  id: string;
  layouts: RoomBoardLayouts;
  height: number;
  group: THREE.Group;
  walls: Partial<Record<WallFace, THREE.Group>>;
  ceiling: THREE.Group | null;
  floor: THREE.Mesh;
  length: number;
  width: number;
  framing: THREE.Group;
  framingMeshes: { mesh: THREE.Mesh; face: WallFace | 'ceiling' }[];
  /** Where each wall stands on the floor, in world x / z. */
  segments: Record<WallFace, FloorSegment>;
  /** 1 = not in the way of the focus room, 0 = faded out of the way (eased). */
  clear: Record<WallFace, number>;
  /** What each wall is drawn at right now (for picking: you click what you see). */
  opacity: Record<WallFace, number>;
}

export const Room3DViewer: React.FC<Room3DViewerProps> = ({
  length,
  width,
  height = 10,
  showHeight = true,
  highlightWalls = false,
  highlightCeiling = false,
  highlightFloor = false,
  wallColor = 'White',
  ceilingColor = 'White',
  compact = false,
  showBoards = true,
  wallOrientation = 'Horizontal',
  slatWallConfig = null,
  showCeiling = false,
  garageConfig = null,
  showFraming: showFramingProp = true,
  prebuiltLayouts,
  fillParent = false,
  editable = false,
  onFaceLock,
  onSurfacePick,
  onSectionPick,
  onProjectionChange,
  onFramingToggle,
  overlay,
  initialLockedFace = null,
  editInsetTop = 0,
  editInsetBottom = 0,
  apiRef,
  enhancedRendering = false,
  contextRooms,
  framingOmit,
  props,
  yard,
  heldOpen,
  accessories,
  roomKey,
  roomOrigin,
  onRoomFocus,
  onFloorDoubleClick,
  suspended = false,
}) => {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  // Either a PerspectiveCamera (default / outside view) or an OrthographicCamera
  // (active during edit-mode lock — see buildOrthoForLockedFace + lockToFace).
  const cameraRef = useRef<THREE.PerspectiveCamera | THREE.OrthographicCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  // Designer only (enhancedRendering): draws the frame from the render settings.
  // Lives as long as the renderer does; re-attached to each rebuilt scene.
  const pipelineRef = useRef<RenderPipeline | null>(null);
  const pipelineFailedRef = useRef(false);
  const roomGroupRef = useRef<THREE.Group | null>(null);
  const isDraggingRef = useRef(false);
  const previousMouseRef = useRef({ x: 0, y: 0 });
  const rotationRef = useRef({ ...INITIAL_ROTATION });
  const orbitDistanceRef = useRef(30);
  const roomCenterRef = useRef(new THREE.Vector3(0, 0, 0));
  const eyeHeightRef = useRef(HUMAN_EYE_FT);
  // How far the user has raised (or lowered) the eye inside the room with
  // Ctrl+wheel, in feet from the default height. It stays — through rebuilds,
  // edit mode and back — until Reset View.
  const eyeLiftRef = useRef(0);
  const eyeY = () => eyeHeightRef.current + eyeLiftRef.current;
  // Whether the viewer is under the planner right now, for code that runs
  // from closures of earlier renders (see renderScene).
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;
  // Where the camera pivots, as an x / z offset from the room's centre. Zero
  // except inside the room, after a double-click on the floor has moved the eye
  // there; every move back out (reset, exit, locking a wall) eases it to zero.
  const pivotOffsetRef = useRef(new THREE.Vector3(0, 0, 0));
  // Floor plans: the other rooms as built into the scene, how visible they are
  // as a whole (they fade out in edit mode), and the room last in focus.
  const contextBuiltRef = useRef<BuiltContextRoom[]>([]);
  // The other rooms as built, kept across rebuilds and used again while
  // everything they were built from is the same object: an edit to the room
  // shown rebuilds that room, not the whole plan. (What is not used again
  // after a rebuild is freed with the old scene.)
  const contextCacheRef = useRef(new Map<string, { inputs: unknown[]; built: BuiltContextRoom }>());
  // Doors (and later windows, garage doors) opened in the view: how open each is
  // (0 closed … 1 open) and where it is heading, by opener key. A view state,
  // not part of the design: it survives rebuilds and walking between rooms.
  const openersRef = useRef(new Map<string, { current: number; target: number }>());
  // The openers this viewer opened itself for the staging (`heldOpen`), to close again when nothing holds them.
  const autoOpenedRef = useRef(new Set<string>());
  const heldOpenRef = useRef(heldOpen);
  heldOpenRef.current = heldOpen;
  const heldOpenKey = (heldOpen ?? []).join('|');
  const movingPartsRef = useRef<{ part: THREE.Object3D; key: string }[]>([]);
  const openerHandlesRef = useRef<THREE.Object3D[]>([]);
  const openerFrameRef = useRef<number | null>(null);
  // Every staged prop in the scene (this room's and the others'): hidden while a wall is being edited.
  const stagedPropsRef = useRef<THREE.Object3D[]>([]);
  // A wall locked from inside the room: exit edit mode goes back in, to this spot.
  const lockedFromInsideRef = useRef(false);
  const preLockEyeRef = useRef<THREE.Vector3 | null>(null);
  const lastLockedFaceRef = useRef<SurfaceFace | null>(null);
  // The layouts as last drawn — click handlers read the openings from it.
  const boardLayoutsRef = useRef<typeof boardLayouts | null>(null);
  const contextRootRef = useRef<THREE.Group | null>(null);
  const contextShownRef = useRef(1);
  const lastRoomRef = useRef<{ key: string; cx: number; cz: number } | null>(null);
  const focusClickTimerRef = useRef<number | null>(null);
  // Camera FOV persists across full scene rebuilds (e.g., when the Designer
  // commits a section edit and prebuiltLayouts changes). createCamera builds a
  // fresh PerspectiveCamera at the default FOV; if the user has zoomed via
  // Ctrl+scroll, the new camera resets that zoom and the wall renders slightly
  // differently than the handles' last-emitted projection expects. Restoring
  // fov from this ref keeps render and projection in sync after a rebuild.
  const cameraFovRef = useRef<number | null>(null);
  // The field of view the user had when a photoreal lens began to be
  // previewed (lensPreview.ts): put back when the preview ends.
  const fovBeforeLensRef = useRef<number | null>(null);

  // Edit-mode camera: swap to an OrthographicCamera while a wall is locked so
  // the editor view is dead-flat (no perspective distortion, no FOV/aspect
  // sensitivity, no "slightly off after zoom/rebuild" alignment issues). The
  // perspective camera is parked here while we use the ortho; we swap back on
  // exitEditMode. orthoZoomRef tracks the user's zoom (Ctrl+scroll) so it
  // survives rebuilds (which re-create the ortho with the saved zoom).
  const perspectiveCameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  // A still being taken (`snapshot`): the camera stands where `poseOverrideRef`
  // says for that one frame, every wall is opaque, the props are shown and the
  // dimension callouts hidden — whatever the view is in.
  const poseOverrideRef = useRef<CameraPose | null>(null);
  const snapshotRef = useRef(false);
  const orthoZoomRef = useRef(1.0);
  // Editing a ceiling plane reached from ANOTHER plane: the screen-up (world)
  // the view was given so it stayed the same way round. Kept while the lock
  // lasts, so a rebuild of the camera (edit, resize, zoom) keeps it too.
  const planeUpRef = useRef<THREE.Vector3 | null>(null);
  // 3D dimension labels group (the perspective-view "20'/12'/10'" callouts).
  // Hidden while a wall is locked in edit mode — the 2D handle overlay draws
  // its own dimension labels that match the locked wall's axes.
  const dimensionsGroupRef = useRef<THREE.Group | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  // The frame asked for to carry a wall fade on to its end (see renderScene).
  const settleFrameRef = useRef<number | null>(null);
  const lightingEntryRef = useRef<LightingRegistryEntry | null>(null);
  const debugEntryRef = useRef<VisualizerDebugEntry | null>(null);
  const floorMeshRef = useRef<THREE.Mesh | null>(null);
  const spinnerRef = useRef<HTMLDivElement>(null);

  // Wall group refs for transparency control
  // The walls, the ceiling (flat, or the group of all its planes), and under a
  // profiled ceiling each plane by its id as well.
  const wallGroupsRef = useRef<Partial<Record<SurfaceFace, THREE.Group>>>({});
  const currentOpacityRef = useRef<TransparencyState>({
    front: 1, back: 1, left: 1, right: 1, ceiling: 1,
  });

  const [isAnimating, setIsAnimatingState] = React.useState(false);
  const setIsAnimating = (v: boolean) => { isAnimatingRef.current = v; setIsAnimatingState(v); };
  const [isDragging, setIsDragging] = React.useState(false);
  const [hasRotated, setHasRotated] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [isInsideRoom, setIsInsideRoom] = React.useState(false);
  const [selectedFace, setSelectedFace] = React.useState<SurfaceFace | null>(null);
  const [sceneReady, setSceneReady] = React.useState(false);
  // The user's Show / Hide Framing choice is THEIRS: remembered across every
  // mount of the viewer (leaving for the floor planner and back, closing and
  // reopening the designer, reloading). The prop only seeds a first visit.
  const [showFraming, setShowFramingState] = React.useState(() => readFramingPreference() ?? showFramingProp);
  // What bare walls and ceilings show (the Materials window): part of the
  // scene's geometry, so a change rebuilds the room.
  const bareFillKeyOf = () => { const s = getRenderSettings(); return `${s.bareWallFill}|${s.bareCeilingFill}`; };
  const [bareFillKey, setBareFillKey] = React.useState(bareFillKeyOf);
  useEffect(() => onRenderSettingsChange(() => { const key = bareFillKeyOf(); setBareFillKey((k) => (k === key ? k : key)); }), []);
  const setShowFraming = useCallback((update: boolean | ((prev: boolean) => boolean)) => {
    setShowFramingState((prev) => {
      const next = typeof update === 'function' ? update(prev) : update;
      writeFramingPreference(next);
      return next;
    });
  }, []);
  const framingGroupRef = useRef<THREE.Group | null>(null);
  // The yard (lawn, driveway, landings, fence) and the staging's yard props
  // are for the RENDER only: built under the room group so the export
  // carries them, never drawn in the view (Rob, 2026-10-14).
  const yardRootRef = useRef<THREE.Group | null>(null);
  // Flat list of every framing mesh + its cached world position. Populated
  // once per build; renderScene reads it and computes per-mesh opacity
  // based on distance from the camera.
  const framingMeshesRef = useRef<{ mesh: THREE.Mesh; worldPos: THREE.Vector3 }[]>([]);
  const isInsideRoomRef = useRef(false); // Ref for immediate access during animation
  const isLockedRef = useRef(false); // true while a wall is selected/locked in edit mode
  const preLockDistanceRef = useRef(0); // orbit distance before locking, restored on exit
  const preLockRotationRef = useRef<{ x: number; y: number } | null>(null); // orbit angle before locking, restored on exit
  const selectedFaceRef = useRef<SurfaceFace | null>(null); // mirrors selectedFace for renderScene (refs, not state)
  const viewerWrapperRef = useRef<HTMLDivElement>(null);
  const outsideDistanceRef = useRef(30);
  // Edit mode tells a click from an orbit drag: the pointer has to travel past a
  // small threshold before it rotates the room, and once it has, the click that
  // ends the gesture is ignored. (Touch drags are reported by the touch controller.)
  const pointerDownRef = useRef({ x: 0, y: 0 });
  const dragMovedRef = useRef(false);
  const touchDragEndedAtRef = useRef(0);
  const isAnimatingRef = useRef(false); // mirrors isAnimating for event handlers

  // Pre-compute board layouts when showBoards is enabled
  const boardLayouts = useMemo(() => {
    if (!showBoards || !showHeight) return null;
    // 3D Designer supplies prebuilt layouts from the section model; otherwise
    // generate from the band config as before.
    if (prebuiltLayouts) return prebuiltLayouts;
    return generateFullRoomLayouts(
      length, width, height,
      wallColor,
      wallOrientation,
      slatWallConfig,
      showCeiling ? ceilingColor : undefined
    );
  }, [prebuiltLayouts, length, width, height, wallColor, wallOrientation, slatWallConfig, showCeiling, ceilingColor, showBoards, showHeight]);
  boardLayoutsRef.current = boardLayouts;

  // Render helper — also updates transparency
  const renderScene = () => {
    // (A ref, not the prop: animation loops and listeners hold a renderScene
    // made on an earlier render — one made while the planner was open would
    // still think the viewer suspended, and every frame of entering the room
    // would be skipped.)
    if (suspendedRef.current || !cameraRef.current || !rendererRef.current || !sceneRef.current) return;
    // A lost WebGL context (the GPU busy elsewhere — Blender rendering — or too
    // many contexts): drawing would throw inside three; wait for 'restored'.
    if (rendererRef.current.getContext().isContextLost()) return;

    // The photoreal lens being previewed (lensPreview.ts): drawn with its
    // field of view while it is set; the user's own back after, and reported
    // (the Photoreal window says what "View" is in mm).
    {
      const cam = cameraRef.current as THREE.PerspectiveCamera;
      if (!(cam as unknown as THREE.OrthographicCamera).isOrthographicCamera) {
        const mm = snapshotRef.current ? null : getLensPreview();
        if (mm !== null) {
          if (fovBeforeLensRef.current === null) fovBeforeLensRef.current = cam.fov;
          const want = verticalFovForMm(mm, cam.aspect);
          if (Math.abs(cam.fov - want) > 1e-3) { cam.fov = want; cam.updateProjectionMatrix(); }
        } else {
          if (fovBeforeLensRef.current !== null) {
            cam.fov = fovBeforeLensRef.current;
            fovBeforeLensRef.current = null;
            cam.updateProjectionMatrix();
          }
          reportViewLens(cam.fov, cam.aspect);
        }
      }
    }

    // Shadows are drawn again only when something that casts one changes
    // (see RenderPipeline.invalidateShadows): these note such a change.
    let shadowsChanged = false;
    const show = (object: THREE.Object3D, visible: boolean) => {
      if (object.visible === visible) return;
      object.visible = visible;
      shadowsChanged = true;
    };
    const casts = (mesh: THREE.Mesh, on: boolean) => {
      if (mesh.castShadow === on && mesh.receiveShadow === on) return;
      mesh.castShadow = on;
      mesh.receiveShadow = on;
      shadowsChanged = true;
    };
    // A fade still on its way asks for another frame — unless a camera
    // animation is running, which draws every frame itself (two full frames a
    // frame was most of the stutter of entering a room).
    const settleFrame = () => {
      settleFrameRef.current = null;
      if (animationFrameRef.current !== null) return;
      renderSceneRef.current();
    };

    // 0. Hide the perspective-view 3D dimension labels while a wall is locked —
    // the 2D handle overlay draws its own edit-mode labels (height on the left,
    // width on the bottom of the locked wall).
    if (dimensionsGroupRef.current) {
      // (And inside a room: they stand outside its corners, seen only through an opening.)
      dimensionsGroupRef.current.visible = !isLockedRef.current && !isInsideRoomRef.current && !snapshotRef.current;
    }
    // Editing a wall: the staging gets out of the way, all of it (but a still shows it).
    for (const prop of stagedPropsRef.current) show(prop, !isLockedRef.current || snapshotRef.current);

    // 1. Position the camera FIRST so per-mesh distance calculations below
    // use the camera position for THIS frame, not the previous frame. Skip
    // entirely when we're in edit mode using the orthographic camera — it's
    // positioned once at lock time and shouldn't be repositioned per frame.
    const isOrtho = (cameraRef.current as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (!isOrtho) {
      if (poseOverrideRef.current) {
        // A still: the camera stands exactly where it was asked to.
        const p = poseOverrideRef.current;
        const cam = cameraRef.current as THREE.PerspectiveCamera;
        cam.position.set(p.position[0], p.position[1], p.position[2]);
        cam.lookAt(p.target[0], p.target[1], p.target[2]);
        cam.fov = p.fov;
      } else if (isInsideRoomRef.current) {
        const lookDir = new THREE.Vector3(
          Math.sin(rotationRef.current.y) * Math.cos(rotationRef.current.x),
          Math.sin(rotationRef.current.x),
          Math.cos(rotationRef.current.y) * Math.cos(rotationRef.current.x)
        );
        // A hair off dead-centre: board joints land on the room's centre lines,
        // and a vertex exactly on the camera plane breaks the rib shading
        // (the side walls go black until the view is turned).
        const eye = roomCenterRef.current.clone().add(pivotOffsetRef.current).add(INSIDE_EYE_OFFSET);
        eye.y = eyeY();
        cameraRef.current.position.copy(eye);
        const lookTarget = eye.add(lookDir);
        cameraRef.current.lookAt(lookTarget);
      } else {
        const pivot = roomCenterRef.current.clone().add(pivotOffsetRef.current);
        const pos = new THREE.Vector3(
          orbitDistanceRef.current * Math.sin(rotationRef.current.y) * Math.cos(rotationRef.current.x) + pivot.x,
          orbitDistanceRef.current * Math.sin(rotationRef.current.x) + pivot.y,
          orbitDistanceRef.current * Math.cos(rotationRef.current.y) * Math.cos(rotationRef.current.x) + pivot.z
        );
        cameraRef.current.position.copy(pos);
        cameraRef.current.lookAt(pivot);
      }
      cameraRef.current.updateProjectionMatrix();
    }

    // 2. Wall transparency (rotation-driven, doesn't read camera position)
    if (showBoards && showHeight) {
      const groups = wallGroupsRef.current;

      let target: TransparencyState;
      if (snapshotRef.current) {
        // A still is of the whole room, whatever the view had faded.
        target = { front: 1, back: 1, left: 1, right: 1, ceiling: 1 };
      } else if (isLockedRef.current && selectedFaceRef.current) {
        // Edit mode: spotlight the selected surface (opaque), fade everything else
        // right down so the focus is uncluttered.
        const sel = selectedFaceRef.current;
        const dim = 0; // fully transparent — the foreground wall must never ghost over the focus
        target = {
          front: sel === 'front' ? 1 : dim,
          back: sel === 'back' ? 1 : dim,
          left: sel === 'left' ? 1 : dim,
          right: sel === 'right' ? 1 : dim,
          ceiling: isCeilingFace(sel) ? 1 : dim,
        };
      } else if (isInsideRoomRef.current) {
        target = { front: 1, back: 1, left: 1, right: 1, ceiling: 1 };
      } else {
        target = calculateWallOpacities(
          rotationRef.current.y,
          rotationRef.current.x
        );
      }
      // The fade moves one step per frame drawn, and frames are only drawn on
      // demand — so while a surface is still short of where it is going, ask
      // for another frame. Otherwise the fade stops wherever the last drag or
      // camera move happened to leave it (walls left half-faded after leaving
      // the room).
      const next = snapshotRef.current ? { ...target } : lerpOpacities(currentOpacityRef.current, target, 0.2);
      let settled = true;
      for (const face of FADING_SURFACES) {
        if (Math.abs(next[face] - target[face]) < 0.005) next[face] = target[face];
        else settled = false;
      }
      currentOpacityRef.current = next;
      if (!settled && settleFrameRef.current === null) settleFrameRef.current = requestAnimationFrame(settleFrame);

      if (groups.front) applyOpacityToGroup(groups.front, currentOpacityRef.current.front);
      if (groups.back) applyOpacityToGroup(groups.back, currentOpacityRef.current.back);
      if (groups.left) applyOpacityToGroup(groups.left, currentOpacityRef.current.left);
      if (groups.right) applyOpacityToGroup(groups.right, currentOpacityRef.current.right);
      if (groups.ceiling) applyOpacityToGroup(groups.ceiling, currentOpacityRef.current.ceiling);

      // Render pipeline: a surface faded right out is switched off, so it casts
      // no shadow and is not there for the occlusion pass to measure against
      // (that pass sees solid geometry, whatever its opacity); one part-way
      // through its fade is drawn but left out of the occlusion.
      if (pipelineRef.current) {
        for (const face of FADING_SURFACES) {
          const group = groups[face];
          if (!group) continue;
          const opacity = currentOpacityRef.current[face];
          show(group, opacity > 0.02);
          group.userData.skipAO = opacity < 0.6;
        }
      }

      // Floor plans: the other rooms. Their walls follow the same cutaway as
      // the focus room's (near walls fade, far walls stay), and any wall that
      // stands between the camera and the focus room fades out of the way, so
      // there is always a clear view in. In edit mode they all step aside.
      const built = contextBuiltRef.current;
      if (built.length > 0) {
        const locked = isLockedRef.current && !!selectedFaceRef.current;
        const inside = isInsideRoomRef.current;
        let contextSettled = true;
        const ease = (from: number, to: number) => {
          const v = from + (to - from) * 0.2;
          if (Math.abs(v - to) < 0.005) return to;
          contextSettled = false;
          return v;
        };
        contextShownRef.current = ease(contextShownRef.current, locked ? 0 : 1);
        const shown = contextShownRef.current;
        // Their lamps dim with them (the lamps themselves stay, so the
        // shaders' light count — compiled in — does not change).
        pipelineRef.current?.setOtherRoomsLit(shown);
        const eye = { x: cameraRef.current.position.x, z: cameraRef.current.position.z };
        const now = currentOpacityRef.current;
        const stepAside = (object: THREE.Object3D, opacity: number) => {
          if (!pipelineRef.current) return;
          show(object, opacity > 0.02);
          object.userData.skipAO = opacity < 0.6;
        };
        for (const room of built) {
          show(room.group, shown > 0.02);
          for (const face of WALL_FACES) {
            const inTheWay = !locked && !inside && wallHidesFocusRoom(room.segments[face], eye, length, width);
            room.clear[face] = ease(room.clear[face], inTheWay ? 0 : 1);
            const opacity = now[face] * room.clear[face] * shown;
            room.opacity[face] = opacity;
            const wall = room.walls[face];
            if (!wall) continue;
            applyOpacityToGroup(wall, opacity);
            stepAside(wall, opacity);
          }
          if (room.ceiling) {
            const opacity = now.ceiling * shown;
            applyOpacityToGroup(room.ceiling, opacity);
            stepAside(room.ceiling, opacity);
          }
        }
        if (!contextSettled && settleFrameRef.current === null) settleFrameRef.current = requestAnimationFrame(settleFrame);
      }

      // 3. Framing fade — per-WALL uniform opacity.
      //
      // Rule:
      //   - The wall most directly facing the camera (highest dot-product)
      //     is FORCED to opacity 0 (fully transparent, always).
      //   - Every other wall gets exactly the "Framing Fade Intensity"
      //     slider value (the non-foreground opacity, uniform across all
      //     three remaining walls).
      //   - The "Framing Max Opacity" slider clamps the result.
      //
      // At slider 100%: foreground = 0, every other wall = 1 (fully opaque).
      // At slider 50%:  foreground = 0, every other wall = 0.5.
      // 360° consistent: only one wall is foreground at any moment, and
      // it's whichever wall is most camera-facing — the other three are
      // identical regardless of rotation.
      const framingMeshes = framingMeshesRef.current;
      if (framingMeshes.length > 0) {
        const cameraX = cameraRef.current.position.x;
        const cameraZ = cameraRef.current.position.z;

        // Camera direction unit vector in floor plane
        let cdx = cameraX;
        let cdz = cameraZ;
        const cdLen = Math.hypot(cdx, cdz);
        if (cdLen > 0.001) {
          cdx /= cdLen;
          cdz /= cdLen;
        } else {
          cdx = 0;
          cdz = -1;
        }

        // Outward wall normals dotted with camera direction
        const facing: Record<WallFace, number> = {
          front: 0 * cdx + -1 * cdz,
          back:  0 * cdx +  1 * cdz,
          left:  -1 * cdx + 0 * cdz,
          right:  1 * cdx + 0 * cdz,
        };

        // Pick the wall most directly facing the camera
        let mostFacing: WallFace = 'front';
        if (facing.back  > facing[mostFacing]) mostFacing = 'back';
        if (facing.left  > facing[mostFacing]) mostFacing = 'left';
        if (facing.right > facing[mostFacing]) mostFacing = 'right';

        const settingsNow = getFramingSettings();
        const intensity = isInsideRoomRef.current ? 1 : settingsNow.nearestOpacity;
        const maxOp = settingsNow.maxOpacity;

        // All non-foreground walls share the slider value, clamped by max.
        const nonForegroundOp = Math.min(intensity, maxOp);

        const wallOpacity: Record<WallFace, number> = {
          front: nonForegroundOp,
          back:  nonForegroundOp,
          left:  nonForegroundOp,
          right: nonForegroundOp,
        };
        // (Inside the room there is no foreground wall: bare framing shows all round.)
        if (!isInsideRoomRef.current) wallOpacity[mostFacing] = 0;

        // Edit mode: show ONLY the locked wall's framing. That framing sits on
        // the exterior side of the locked wall (further from the camera than
        // the boards), so the boards occlude it and the framing reads as
        // BEHIND the product — visible through gaps / between sections.
        // Other walls' framing is hidden because (a) those walls themselves
        // are transparent in edit mode and (b) some of them are physically
        // CLOSER to the camera than the locked wall, so showing their framing
        // would put it in front of the product.
        // With the ceiling locked there is no wall to stand behind, so all of
        // it is hidden (every comparison below is false).
        if (isLockedRef.current && selectedFaceRef.current) {
          const sel = selectedFaceRef.current;
          wallOpacity.front = sel === 'front' ? maxOp : 0;
          wallOpacity.back  = sel === 'back'  ? maxOp : 0;
          wallOpacity.left  = sel === 'left'  ? maxOp : 0;
          wallOpacity.right = sel === 'right' ? maxOp : 0;
        }

        // Rafters and the ridge (a profiled ceiling's framing) show with the
        // other walls' framing, and in edit mode only behind a locked ceiling plane.
        const ceilingFramingOp = isLockedRef.current && selectedFaceRef.current
          ? (isCeilingFace(selectedFaceRef.current) ? maxOp : 0)
          : nonForegroundOp;
        for (let i = 0; i < framingMeshes.length; i++) {
          const mesh = framingMeshes[i].mesh;
          const face = (mesh.userData.face as WallFace | 'ceiling') || 'front';
          const opacity = face === 'ceiling' ? ceilingFramingOp : wallOpacity[face];

          casts(mesh, opacity > 0.5);
          if (pipelineRef.current) {
            // As for the walls above.
            show(mesh, opacity > 0.02);
            mesh.userData.skipAO = opacity < 0.6;
          }
          const mat = mesh.material;
          if (mat instanceof THREE.MeshStandardMaterial) {
            // Material is created with transparent:true once and never
            // toggled at runtime (avoids shader-recompile gotcha). We just
            // animate opacity — at opacity=1 the pixels still render solid.
            mat.opacity = opacity;
            mat.depthWrite = opacity > 0.9;
          }
        }
        // The other rooms' framing: the same per-wall rule, and out of the way with its wall.
        for (const room of contextBuiltRef.current) {
          for (const { mesh, face } of room.framingMeshes) {
            const opacity = (face === 'ceiling' ? ceilingFramingOp : wallOpacity[face] * room.clear[face]) * contextShownRef.current;
            casts(mesh, opacity > 0.5);
            if (pipelineRef.current) {
              show(mesh, opacity > 0.02);
              mesh.userData.skipAO = opacity < 0.6;
            }
            const mat = mesh.material;
            if (mat instanceof THREE.MeshStandardMaterial) {
              mat.opacity = opacity;
              mat.depthWrite = opacity > 0.9;
            }
          }
        }
        if (!pipelineRef.current) rendererRef.current.shadowMap.needsUpdate = true;
      }
    }
    // Something that casts a shadow came, went or changed: the maps are drawn
    // again (once). Nothing changed → the lamps' shadows are kept as they are.
    if (shadowsChanged) pipelineRef.current?.invalidateShadows();

    if (pipelineRef.current) {
      try {
        pipelineRef.current.render(sceneRef.current, cameraRef.current);
        return;
      } catch (error) {
        // A pass this GPU or driver cannot run: give the pipeline up for this
        // viewer and draw the plain way from here on, rather than show nothing.
        console.warn('Room3DViewer: render pipeline failed; drawing without it.', error);
        pipelineRef.current.dispose();
        pipelineRef.current = null;
        pipelineFailedRef.current = true;
        rendererRef.current.setRenderTarget(null);
        rendererRef.current.shadowMap.autoUpdate = true;
      }
    }
    rendererRef.current.render(sceneRef.current, cameraRef.current);
  };
  // Always the latest closure, for callbacks registered once.
  const renderSceneRef = useRef(renderScene);
  renderSceneRef.current = renderScene;

  // The last scene's GPU resources, freed once the next scene has drawn (see
  // the setup effect's cleanup) — or on unmount.
  const pendingDisposeRef = useRef<{ dispose(): void }[]>([]);
  // Shader compiles still in flight (renderer.compileAsync polls each
  // material's program until it is ready): a material freed under one of them
  // crashes the poll ("reading 'isReady'"), so nothing is freed until they
  // have all settled — which they do, since their programs are kept.
  const compilesRef = useRef<Set<Promise<unknown>>>(new Set());
  const flushDisposals = () => {
    const list = pendingDisposeRef.current;
    if (list.length === 0) return;
    pendingDisposeRef.current = [];
    const dispose = () => { for (const d of list) d.dispose(); };
    if (compilesRef.current.size === 0) dispose();
    else Promise.all([...compilesRef.current]).then(dispose);
  };

  // Main scene setup
  useEffect(() => {
    if (!containerRef.current) return;
    // Not on screen (the planner is open over the viewer): nothing to build
    // or draw. The renderer, its context, programs and textures are kept —
    // that is the point — and the scene is built once this flips back.
    if (suspended) return;

    // Immediately show spinner via DOM before any scene work —
    // this prevents the flash of a half-built scene.
    // Not when the render pipeline is rebuilding (every designer edit): that
    // scene is built, lit and drawn before this effect returns, so there is
    // nothing half-built to hide — and a spinner on every edit would flicker.
    const rebuildInPlace = pipelineRef.current !== null;
    if (!rebuildInPlace) {
      if (spinnerRef.current) {
        spinnerRef.current.style.transition = 'none';
        spinnerRef.current.style.opacity = '1';
        spinnerRef.current.style.pointerEvents = 'auto';
      }
      setSceneReady(false);
    }
    // The spinner is raised straight on the DOM above, so it has to be lowered
    // there too: if React folds "not ready" and "ready" into one render it sees
    // no change and would leave the spinner up for good.
    const showScene = () => {
      renderScene();
      if (spinnerRef.current) {
        spinnerRef.current.style.transition = 'opacity 0.5s ease-in-out';
        spinnerRef.current.style.opacity = '0';
        spinnerRef.current.style.pointerEvents = 'none';
      }
      setSceneReady(true);
    };

    const container = containerRef.current;
    const roomLength = length;
    const roomWidth = width;
    const roomHeight = showHeight ? height : 0.1;

    // Scene
    const scene = createScene();
    sceneRef.current = scene;

    // Lighting
    const sceneLights = addLighting(scene);

    // Interior light created after roomGroup (see below)

    // Camera. (Inside a room the eye has a wider view than the default; a rebuild
    // there — walking into the next room of a plan — keeps it.)
    const previous = cameraRef.current as THREE.PerspectiveCamera | null;
    const insideFov = isInsideRoomRef.current && previous && !(previous as unknown as THREE.OrthographicCamera).isOrthographicCamera ? previous.fov : null;
    const { camera, distance } = createCamera(
      container.clientWidth,
      container.clientHeight,
      roomLength,
      roomWidth,
      roomHeight
    );
    const roomCenterY = showHeight ? height / 2 : 0;
    roomCenterRef.current.set(0, roomCenterY, 0);
    eyeHeightRef.current = showHeight ? insideEyeHeight(height) : 0;
    // Don't reset zoom while a wall is locked (e.g. rebuild after a live edit) —
    // the locked head-on zoom must stay fixed.
    if (!isLockedRef.current) orbitDistanceRef.current = distance;
    cameraRef.current = camera;

    // Renderer — create ONCE per Room3DViewer mount and reuse across every
    // scene rebuild. Browsers cap concurrent WebGL contexts; previously this
    // useEffect ran on every `boardLayouts` change (i.e., every section edit
    // in the Designer), churning out new WebGL contexts that pushed OTHER
    // viewers' contexts past the cap (the wizard preview going blank after
    // exiting Designer3D was the symptom). The renderer is disposed only on
    // component UNMOUNT in a separate effect below.
    if (!rendererRef.current) {
      const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      configureRenderer(r, showHeight);
      // Checking every program for errors after linking is a synchronous
      // round trip to the driver per program: for development only.
      r.debug.checkShaderErrors = process.env.NODE_ENV !== 'production';
      // Per-material clipping planes (used by buildExtrudedSlatWallSection to
      // hide the topmost panel's tongue above section.top).
      r.localClippingEnabled = true;
      r.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(r.domElement);
      // preventDefault lets the browser restore the context instead of leaving
      // it dead; on restore, three rebuilds its programs on the next frame.
      r.domElement.addEventListener('webglcontextlost', (e) => { e.preventDefault(); });
      r.domElement.addEventListener('webglcontextrestored', () => { renderSceneRef.current(); });
      rendererRef.current = r;
    }
    const renderer = rendererRef.current;
    // (WebGL 2 only: the pipeline's passes need it. Without it the designer
    // draws the plain way, like the wizard's viewers.)
    if (enhancedRendering && !pipelineRef.current && !pipelineFailedRef.current && renderer.capabilities.isWebGL2) {
      pipelineRef.current = new RenderPipeline(renderer, () => renderSceneRef.current());
    }
    const pipeline = pipelineRef.current;

    // Everything that draws the new scene goes through `whenReady`: the first
    // time it is asked, the scene (complete by then — lamps included) has its
    // shaders compiled, in parallel and off the main thread where the driver
    // can, while the last frame stays on screen. See the first frame below.
    let cancelled = false;
    let ready: Promise<unknown> | null = null;
    const whenReady = (fn: () => void) => {
      if (!ready) {
        const cam = cameraRef.current;
        if (pipeline && cam) {
          const compile = compileScene(renderer, scene, cam, () => cancelled);
          compilesRef.current.add(compile);
          compile.then(() => compilesRef.current.delete(compile));
          ready = compile;
        } else {
          ready = Promise.resolve();
        }
      }
      ready.then(() => { if (!cancelled) fn(); });
    };

    // Room group
    const roomGroup = new THREE.Group();
    roomGroupRef.current = roomGroup;
    scene.add(roomGroup);

    // Build room
    buildRoom(roomGroup, roomLength, roomWidth, roomHeight);

    // Interior point light — added to roomGroup so it's in room-local coordinates
    const rigBase = {
      scene,
      roomGroup,
      floor: floorMeshRef.current,
      ...sceneLights,
      dims: {
        length: roomLength, width: roomWidth, height: roomHeight,
        ceilingAt: (x: number, z: number) => ceilingHeightAt(boardLayouts ?? undefined, x, z, roomHeight),
        ceilingNormalAt: (x: number, z: number) => ceilingNormalAt(boardLayouts ?? undefined, x, z),
      },
      otherFloors: contextBuiltRef.current.map((r) => ({ mesh: r.floor, length: r.length, width: r.width, key: `:${r.id}` })),
      footprints: [
        { x: 0, z: 0, length: roomLength, width: roomWidth },
        ...(contextRooms ?? []).map((r) => ({ x: r.offsetX, z: r.offsetZ, length: r.length, width: r.width })),
      ],
      sheetOrigin: roomOrigin,
      otherRooms: contextBuiltRef.current.map((r) => ({
        key: r.id, group: r.group, length: r.length, width: r.width, height: r.height,
        ceilingAt: (x: number, z: number) => ceilingHeightAt(r.layouts, x, z, r.height),
        ceilingNormalAt: (x: number, z: number) => ceilingNormalAt(r.layouts, x, z),
      })),
    };
    if (showHeight) {
      const interiorLight = addInteriorLight(roomGroup, roomLength, roomWidth, roomHeight);
      let hemiLight: THREE.HemisphereLight | null = null;
      scene.traverse(child => {
        if (child instanceof THREE.HemisphereLight) hemiLight = child;
      });

      const entry: LightingRegistryEntry = {
        viewerType: 'room',
        light: interiorLight,
        hemiLight,
        probe: null,
        envMap: null,
        floorMesh: floorMeshRef.current,
        renderer,
        scene,
        roomGroup,
        renderScene,
        getRotation: () => ({ ...rotationRef.current }),
        roomDimensions: { length: roomLength, width: roomWidth, height: roomHeight },
      };
      lightingEntryRef.current = entry;
      registerLight(entry);

      if (pipeline) {
        // The render settings own the lights, the environment and the materials
        // from here (the environment arrives through the pipeline, which asks
        // for a redraw when a photo finishes loading).
        pipeline.attach({ ...rigBase, interior: interiorLight });
        whenReady(() => requestAnimationFrame(showScene));
      } else {
      // Ensure scene becomes visible even if probe loading fails
      const readyTimeout = setTimeout(() => {
        renderScene();
        setSceneReady(true);
      }, 2000);

      // Load light probe from garage env image
      addLightProbe(scene, renderer, ENV_IMAGES[2].path, (setup) => {
        clearTimeout(readyTimeout);

        entry.probe = setup.probe;
        entry.probe.intensity = 0.4;
        entry.envMap = setup.envMap;

        // scene.environment applies envMap lighting/reflections to all materials
        scene.environment = setup.envMap;

        // Set default envMapIntensity on wall materials (live traversal)
        roomGroup.traverse(child => {
          if (child === floorMeshRef.current) return;
          if (child instanceof THREE.Mesh && child.material instanceof THREE.MeshStandardMaterial) {
            child.material.envMapIntensity = 0.5;
          }
        });

        // Notify the panel that probe is now available
        registerLight(entry);
        requestAnimationFrame(() => {
          renderScene();
          setSceneReady(true);
        });
      });
      }
    } else {
      // No interior light (ceiling-only mode) — ready immediately
      if (pipeline) pipeline.attach({ ...rigBase, interior: null });
      whenReady(() => requestAnimationFrame(showScene));
    }

    // Restore the user's FOV from before this rebuild. createCamera built the
    // camera at the default FOV; if the user had zoomed (Ctrl+scroll / pinch) we
    // would lose that on every section commit and the handle overlay (last
    // emitted under the old FOV) would no longer match the rendered wall.
    if (cameraFovRef.current !== null) {
      camera.fov = cameraFovRef.current;
      camera.updateProjectionMatrix();
    } else if (insideFov !== null) {
      camera.fov = insideFov;
      camera.updateProjectionMatrix();
    }

    // If we're currently locked in edit mode, the ACTIVE camera should remain
    // the orthographic editor camera — not this fresh perspective. Park the
    // perspective for exitEditMode to restore later, and re-create the ortho
    // with the saved zoom so the editor view survives a section-commit rebuild.
    if (isLockedRef.current && selectedFaceRef.current) {
      perspectiveCameraRef.current = camera;
      const ortho = buildOrthoForLockedFace(selectedFaceRef.current);
      if (ortho) {
        cameraRef.current = ortho;
      }
    }

    // Zoom to fit: calculate optimal distance to fill viewport
    // (The focus room alone: the plan's other rooms are not what the view is framed on.)
    // (Nor the yard, nor the staging's yard props: a lawn 80 ft across with trees on it would push the orbit out three times as far.)
    const contextRoot = contextRootRef.current;
    const yardRoot = yardRootRef.current;
    const stagedYards: { group: THREE.Object3D; parent: THREE.Object3D }[] = [];
    roomGroup.traverse((o) => { if (o.name === 'staging-yard' && o.parent) stagedYards.push({ group: o, parent: o.parent }); });
    if (contextRoot) roomGroup.remove(contextRoot);
    if (yardRoot) roomGroup.remove(yardRoot);
    for (const s of stagedYards) s.parent.remove(s.group);
    const fitDistance = calculateZoomToFit(camera, roomGroup, rotationRef.current, roomCenterRef.current);
    if (contextRoot) roomGroup.add(contextRoot);
    if (yardRoot) roomGroup.add(yardRoot);
    for (const s of stagedYards) s.parent.add(s.group);
    const previousDistance = orbitDistanceRef.current;
    // Preserve the locked head-on zoom across rebuilds (live edits); only re-fit
    // the room when not locked.
    if (!isLockedRef.current) orbitDistanceRef.current = fitDistance;
    outsideDistanceRef.current = fitDistance;

    // Floor plans: another room has come into focus. The scene has just been
    // rebuilt around it; start the camera where it was — which is now off to
    // one side, by the distance between the two rooms — and travel from there
    // to the default view of the new room (below, once the scene is drawn).
    const centre = roomOrigin ? { cx: roomOrigin.x + roomLength / 2, cz: roomOrigin.z + roomWidth / 2 } : null;
    const lastRoom = lastRoomRef.current;
    const changedRoom = !!roomKey && !!centre && !!lastRoom && lastRoom.key !== roomKey;
    const arriving = changedRoom && !isLockedRef.current && !isInsideRoomRef.current;
    if (arriving && centre && lastRoom) {
      pivotOffsetRef.current.set(lastRoom.cx - centre.cx, 0, lastRoom.cz - centre.cz);
      orbitDistanceRef.current = previousDistance;
    } else if (changedRoom && isInsideRoomRef.current && centre && lastRoom) {
      // Walked into another room: the scene is now centred on it, the eye has not moved.
      pivotOffsetRef.current.set(
        pivotOffsetRef.current.x + lastRoom.cx - centre.cx, 0, pivotOffsetRef.current.z + lastRoom.cz - centre.cz,
      );
    } else if (changedRoom) {
      pivotOffsetRef.current.set(0, 0, 0);
    }
    lastRoomRef.current = roomKey && centre ? { key: roomKey, ...centre } : null;

    // Compute initial transparency and render
    if (showBoards && showHeight) {
      const groups = wallGroupsRef.current;
      let initialOpacity: TransparencyState;
      if (isLockedRef.current && selectedFaceRef.current) {
        // Preserve the edit-mode spotlight across rebuilds (live edits): the
        // selected surface stays opaque, everything else fully transparent — so
        // unselected walls don't flash back in after an edit.
        const sel = selectedFaceRef.current;
        initialOpacity = {
          front: sel === 'front' ? 1 : 0,
          back: sel === 'back' ? 1 : 0,
          left: sel === 'left' ? 1 : 0,
          right: sel === 'right' ? 1 : 0,
          ceiling: isCeilingFace(sel) ? 1 : 0,
        };
        if (floorMeshRef.current) floorMeshRef.current.visible = !isCeilingFace(sel);
      } else if (isInsideRoomRef.current) {
        // Rebuilt with the eye inside (walked into the next room): every wall stands.
        initialOpacity = { front: 1, back: 1, left: 1, right: 1, ceiling: 1 };
      } else {
        initialOpacity = calculateWallOpacities(rotationRef.current.y, rotationRef.current.x);
      }
      currentOpacityRef.current = initialOpacity;
      if (groups.front) applyOpacityToGroup(groups.front, initialOpacity.front);
      if (groups.back) applyOpacityToGroup(groups.back, initialOpacity.back);
      if (groups.left) applyOpacityToGroup(groups.left, initialOpacity.left);
      if (groups.right) applyOpacityToGroup(groups.right, initialOpacity.right);
      if (groups.ceiling) applyOpacityToGroup(groups.ceiling, initialOpacity.ceiling);
    }

    // The first frame of the new scene. With the pipeline, its shaders are
    // compiled first — in parallel, off the main thread where the driver can
    // (KHR_parallel_shader_compile) — while the last frame stays on screen;
    // drawn straight away, every program the scene needs would be compiled
    // synchronously inside this effect, and that was the freeze on every
    // room change. The old scene's materials are freed only once the new
    // one has drawn (flushDisposals), so a program both use is kept, not
    // compiled again.
    whenReady(() => {
    renderScene();
    requestAnimationFrame(flushDisposals);
    // After a rebuild while locked, re-emit the projection so the handle overlay
    // matches the freshly-recreated ortho camera (the projection was emitted by
    // the previous setup's ortho — same math, but emitted under the previous
    // camera instance; safer to refresh).
    recomputeProjectionRef.current();

    // Floor plans: the journey to the newly focused room (set up above).
    if (arriving) {
      setIsAnimating(true);
      const startRotation = { ...rotationRef.current };
      const startPivot = pivotOffsetRef.current.clone();
      const startDistance = orbitDistanceRef.current;
      const startTime = Date.now();
      const travel = () => {
        const progress = Math.min((Date.now() - startTime) / FOCUS_TRAVEL_MS, 1);
        const eased = easeInOutCubic(progress);
        rotationRef.current = {
          x: startRotation.x + shortestAngleDelta(startRotation.x, INITIAL_ROTATION.x) * eased,
          y: startRotation.y + shortestAngleDelta(startRotation.y, INITIAL_ROTATION.y) * eased,
        };
        orbitDistanceRef.current = startDistance + (fitDistance - startDistance) * eased;
        pivotOffsetRef.current.copy(startPivot).multiplyScalar(1 - eased);
        renderSceneRef.current();
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(travel);
        } else {
          rotationRef.current = { ...INITIAL_ROTATION };
          pivotOffsetRef.current.set(0, 0, 0);
          setIsAnimating(false);
          setHasRotated(false);
          animationFrameRef.current = null;
          renderSceneRef.current();
        }
      };
      animationFrameRef.current = requestAnimationFrame(travel);
    }
    });

    // Register with debug registry for snapshot tooling
    const debugEntry: VisualizerDebugEntry = {
      id: generateVisualizerId('room', `${roomLength}x${roomWidth}x${roomHeight}`),
      kind: 'room',
      renderer,
      scene,
      camera,
      roomGroup,
      renderScene,
      pipeline: pipeline ?? undefined,
    };
    debugEntryRef.current = debugEntry;
    registerVisualizer(debugEntry);

    // Resize handler
    const handleResize = () => syncSizeRef.current();

    window.addEventListener('resize', handleResize);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        // Cut short by a rebuild (or unmount): it will not get to say it has finished.
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
        setIsAnimating(false);
      }
      if (settleFrameRef.current !== null) {
        cancelAnimationFrame(settleFrameRef.current);
        settleFrameRef.current = null;
      }
      if (focusClickTimerRef.current !== null) {
        window.clearTimeout(focusClickTimerRef.current);
        focusClickTimerRef.current = null;
      }
      if (lightingEntryRef.current) {
        unregisterLight(lightingEntryRef.current);
        lightingEntryRef.current = null;
      }
      if (debugEntryRef.current) {
        unregisterVisualizer(debugEntryRef.current);
        debugEntryRef.current = null;
      }
      // NOTE: do NOT dispose the renderer here — see the renderer creation
      // comment above. Disposal happens on component unmount only.
      wallGroupsRef.current = {};
      // What this scene put on the GPU is freed — but only once the NEXT scene
      // has drawn its first frame (flushDisposals), or on unmount. Freed now,
      // every shader program would go with it (three keeps a program only
      // while a material uses it) and the next scene would compile them all
      // again. (Sprites share cached materials and are left alone.)
      // The other rooms are kept for the next build (contextCacheRef): out of
      // this scene before it is freed. The next build frees what it does not use.
      for (const built of contextBuiltRef.current) built.group.removeFromParent();
      const pending = pendingDisposeRef.current;
      disposablesOf(roomGroup, pending);
      scene.traverse((child) => {
        if ((child as THREE.Light).isLight) pending.push(child as THREE.Light);
      });
    };
  }, [length, width, height, showHeight, highlightWalls, highlightCeiling, highlightFloor, wallColor, ceilingColor, boardLayouts, showBoards, garageConfig, contextRooms, framingOmit, roomKey, props, yard, heldOpenKey, accessories, suspended, bareFillKey]);

  // Match the renderer and cameras to the container's current size. In edit
  // mode the active camera is the ortho editor camera, whose frustum was sized
  // for the old aspect — rebuild it (the user's zoom is kept) so the locked
  // surface stays framed, then re-emit the projection so the handle overlay
  // lands back on it. The parked perspective camera is kept in step too, ready
  // for when edit mode exits.
  const syncSize = () => {
    const container = containerRef.current;
    const renderer = rendererRef.current;
    const cam = cameraRef.current;
    if (!container || !renderer || !cam) return;
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w <= 0 || h <= 0) return;
    if ((cam as THREE.OrthographicCamera).isOrthographicCamera) {
      if (perspectiveCameraRef.current) {
        perspectiveCameraRef.current.aspect = w / h;
        perspectiveCameraRef.current.updateProjectionMatrix();
      }
      const face = selectedFaceRef.current;
      const ortho = face ? buildOrthoForLockedFace(face) : null;
      if (ortho) cameraRef.current = ortho;
    } else {
      (cam as THREE.PerspectiveCamera).aspect = w / h;
      cam.updateProjectionMatrix();
    }
    renderer.setSize(w, h);
    renderScene();
    recomputeProjectionRef.current();
  };
  // Always the latest closure, for listeners attached once.
  const syncSizeRef = useRef(syncSize);
  syncSizeRef.current = syncSize;

  // Edit mode: the designer's layout can change the container's size without a
  // window resize (panels opening, fullscreen), so watch the container itself.
  useEffect(() => {
    const container = containerRef.current;
    if (!editable || !container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => syncSizeRef.current());
    observer.observe(container);
    return () => observer.disconnect();
  }, [editable]);

  // Renderer disposal on component UNMOUNT only. Kept separate from the main
  // setup effect (which re-runs on every prop / boardLayouts change) so the
  // WebGL context survives across scene rebuilds.
  useEffect(() => {
    return () => {
      const container = containerRef.current;
      const renderer = rendererRef.current;
      contextCacheRef.current.forEach((entry) => disposablesOf(entry.built.group, pendingDisposeRef.current));
      contextCacheRef.current.clear();
      flushDisposals();
      pipelineRef.current?.dispose();
      pipelineRef.current = null;
      if (renderer) {
        if (container && renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
        renderer.dispose();
        // Give the context back now rather than when the garbage collector
        // gets to it: browsers allow only so many at once.
        renderer.forceContextLoss();
        rendererRef.current = null;
      }
    };
  }, []);

  // The previewed photoreal lens changed: redraw with it (or without it), and
  // the handles follow the new projection.
  useEffect(() => {
    let last = getLensPreview();
    return onLensChange(() => {
      const now = getLensPreview();
      if (now === last) return;
      last = now;
      renderSceneRef.current();
      recomputeProjectionRef.current();
    });
  }, []);

  // Render settings changed (a slider in the designer's render windows): push
  // them into the scene and redraw. No rebuild.
  useEffect(() => {
    if (!enhancedRendering) return;
    return onRenderSettingsChange(() => {
      pipelineRef.current?.apply();
      renderSceneRef.current();
    });
  }, [enhancedRendering]);

  // Wheel zoom + touch controls
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const wheelHandler = (e: WheelEvent) => {
      // The wizard's viewers sit in a scrolling page, so there the wheel only
      // zooms with Ctrl held. The designer fills the window: the wheel zooms
      // outright, except on a locked surface (where it is easy to nudge by
      // accident while working on a section).
      const wheelZooms = editable && !isLockedRef.current;
      if (!e.ctrlKey && !wheelZooms) return;
      e.preventDefault();
      const cam = cameraRef.current;
      if (!cam) return;
      // Inside the room (designer), Ctrl+wheel raises and lowers the eye
      // instead — scroll up to rise — between a foot off the floor and a
      // little under the ceiling. It holds there until Reset View.
      if (e.ctrlKey && editable && isInsideRoomRef.current && !isLockedRef.current) {
        const lift = eyeLiftRef.current - e.deltaY * EYE_LIFT_PER_WHEEL_UNIT;
        const base = eyeHeightRef.current;
        const ceiling = roomCenterRef.current.y * 2; // the room's centre is half way up (refs: this handler is bound once)
        eyeLiftRef.current = Math.max(1 - base, Math.min(ceiling - 0.5 - base, lift));
        renderScene();
        recomputeProjectionRef.current();
        return;
      }
      if ((cam as THREE.OrthographicCamera).isOrthographicCamera) {
        // Ortho zoom — scale the frustum (deltaY > 0 = scroll down = zoom out).
        const ortho = cam as THREE.OrthographicCamera;
        const factor = Math.pow(1.001, e.deltaY);
        ortho.left *= factor;
        ortho.right *= factor;
        ortho.top *= factor;
        ortho.bottom *= factor;
        orthoZoomRef.current /= factor; // persist across rebuilds
        ortho.updateProjectionMatrix();
      } else {
        const persp = cam as THREE.PerspectiveCamera;
        persp.fov = applyZoom(persp.fov, e.deltaY);
        cameraFovRef.current = persp.fov; // persist across rebuilds
        persp.updateProjectionMatrix();
      }
      renderScene();
      // Camera changed -> projection changed -> handle overlay must follow.
      recomputeProjectionRef.current();
    };

    container.addEventListener('wheel', wheelHandler, { passive: false });

    const detachTouch = attachTouchControls({
      container,
      getRotation: () => rotationRef.current,
      setRotation: (r) => { if (isLockedRef.current) return; rotationRef.current = r; },
      getFov: () => {
        const cam = cameraRef.current;
        if (!cam) return 50;
        // OrthographicCamera has no fov — return a placeholder so pinch-gesture
        // math doesn't blow up; setFov ignores writes while ortho is active.
        return (cam as THREE.OrthographicCamera).isOrthographicCamera ? 50 : (cam as THREE.PerspectiveCamera).fov;
      },
      setFov: (fov) => {
        const cam = cameraRef.current;
        if (!cam) return;
        // While the editor's ortho camera is active, ignore fov writes — Ctrl+
        // scroll is the supported zoom path in edit mode. (Pinch-to-zoom for
        // ortho is a later polish; we'd convert the pinch delta to a frustum
        // scale instead of writing to fov.)
        if ((cam as THREE.OrthographicCamera).isOrthographicCamera) return;
        const persp = cam as THREE.PerspectiveCamera;
        persp.fov = fov;
        cameraFovRef.current = fov; // persist across rebuilds
        persp.updateProjectionMatrix();
      },
      onUpdate: () => { renderScene(); recomputeProjectionRef.current(); },
      onDragStart: () => setHasRotated(true),
      onGestureEnd: (moved) => { if (moved) touchDragEndedAtRef.current = Date.now(); },
    });

    return () => {
      container.removeEventListener('wheel', wheelHandler);
      detachTouch();
    };
  }, [editable]);

  const buildRoom = (
    roomGroup: THREE.Group,
    roomLength: number,
    roomWidth: number,
    roomHeight: number
  ) => {
    // Dispose all old geometry/materials before rebuilding
    roomGroup.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else if (child.material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.LineSegments) {
        child.geometry?.dispose();
        if (child.material instanceof THREE.Material) child.material.dispose();
      }
    });
    while (roomGroup.children.length > 0) roomGroup.remove(roomGroup.children[0]);
    wallGroupsRef.current = {};

    // Floor
    const floorMat = createFloorMaterial(highlightFloor, () => renderScene());
    // In a floor plan each floor runs half a wall's thickness under its walls,
    // so the floors of joined rooms meet.
    const floorPad = contextRooms?.length ? PLAN_WALL_GAP_FT : 0;
    const floorGeo = new THREE.PlaneGeometry(roomLength + floorPad, roomWidth + floorPad);
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(roomLength / 2, 0, roomWidth / 2);
    floor.receiveShadow = true;
    floorMeshRef.current = floor;
    roomGroup.add(floor);

    if (showHeight) {
      if (boardLayouts && showBoards) {
        // Board-level rendering with per-wall groups for transparency
        const wallFaces: WallFace[] = ['front', 'right', 'back', 'left'];
        const faceKeys = ['front', 'right', 'back', 'left'] as const;

        for (let i = 0; i < boardLayouts.walls.length; i++) {
          const wallGroup = buildWallMesh(
            boardLayouts.walls[i],
            wallFaces[i],
            roomLength, roomWidth, roomHeight,
            (x, z) => ceilingHeightAt(boardLayouts, x, z, roomHeight),
            getRenderSettings().bareWallFill,
          );
          roomGroup.add(wallGroup);
          (wallGroupsRef.current as any)[faceKeys[i]] = wallGroup;
        }

        // Garage doors — overlay on the wall selected by onWallType
        if (garageConfig?.enabled && garageConfig.numDoors > 0) {
          const isLengthWall = garageConfig.onWallType === 'LengthWall';
          const face: WallFace = isLengthWall ? 'front' : 'right';
          const wallLen = isLengthWall ? roomLength : roomWidth;
          const doorGroup = createGarageDoorGroup(
            garageConfig,
            wallLen,
            face,
            roomLength,
            roomWidth
          );
          roomGroup.add(doorGroup);
        }

        // Ceiling — only show if ceiling is included
        if (boardLayouts.ceiling) {
          const ceilingGroup = buildCeilingMesh(boardLayouts.ceiling, roomLength, roomWidth, roomHeight, getRenderSettings().bareCeilingFill);
          roomGroup.add(ceilingGroup);
          wallGroupsRef.current.ceiling = ceilingGroup;
        } else if (boardLayouts.ceilingPlanes?.length) {
          // A profiled ceiling: all its planes fade and lock as "the ceiling";
          // each plane is a surface of its own to look at and edit.
          const ceilingGroup = buildCeilingPlanesMesh(boardLayouts.ceilingPlanes, roomLength, roomWidth, getRenderSettings().bareCeilingFill);
          roomGroup.add(ceilingGroup);
          wallGroupsRef.current.ceiling = ceilingGroup;
          for (const child of ceilingGroup.children) {
            if (typeof child.userData.ceilingPlane === 'string') wallGroupsRef.current[child.userData.ceilingPlane as SurfaceFace] = child as THREE.Group;
          }
        }
      } else {
        // Legacy mode
        const wallMaterial = createLegacyWallMaterial(wallColor, highlightWalls);
        const ceilingMat = createLegacyCeilingMaterial(ceilingColor, highlightCeiling);

        const frontGeo = new THREE.PlaneGeometry(roomLength, roomHeight);
        const frontWall = new THREE.Mesh(frontGeo, wallMaterial.clone());
        frontWall.position.set(roomLength / 2, roomHeight / 2, 0);
        addBorders(frontGeo, frontWall);
        roomGroup.add(frontWall);

        const backGeo = new THREE.PlaneGeometry(roomLength, roomHeight);
        const backWall = new THREE.Mesh(backGeo, wallMaterial.clone());
        backWall.position.set(roomLength / 2, roomHeight / 2, roomWidth);
        backWall.rotation.y = Math.PI;
        addBorders(backGeo, backWall);
        roomGroup.add(backWall);

        const leftGeo = new THREE.PlaneGeometry(roomWidth, roomHeight);
        const leftWall = new THREE.Mesh(leftGeo, wallMaterial.clone());
        leftWall.position.set(0, roomHeight / 2, roomWidth / 2);
        leftWall.rotation.y = Math.PI / 2;
        addBorders(leftGeo, leftWall);
        roomGroup.add(leftWall);

        const rightGeo = new THREE.PlaneGeometry(roomWidth, roomHeight);
        const rightWall = new THREE.Mesh(rightGeo, wallMaterial.clone());
        rightWall.position.set(roomLength, roomHeight / 2, roomWidth / 2);
        rightWall.rotation.y = -Math.PI / 2;
        addBorders(rightGeo, rightWall);
        roomGroup.add(rightWall);

        const ceilingGeo = new THREE.PlaneGeometry(roomLength, roomWidth);
        const ceiling = new THREE.Mesh(ceilingGeo, ceilingMat);
        ceiling.rotation.x = Math.PI / 2;
        ceiling.position.set(roomLength / 2, roomHeight, roomWidth / 2);
        addBorders(ceilingGeo, ceiling);
        roomGroup.add(ceiling);
      }
    }

    // Staging: the props the designer placed in this room (hung ones go into
    // their wall's group, so they fade with it).
    if (showHeight && showBoards && props?.length) {
      addStagedProps(roomGroup, wallGroupsRef.current, props, roomLength, roomWidth, SLAT_FACE_Z, renderScene);
    }
    // The SlatWall accessories the user hung: on their walls, against the
    // slats, and NOT hidden in edit mode (that is where they are placed).
    if (showHeight && showBoards && accessories?.length) {
      for (const a of accessories) {
        const wall = wallGroupsRef.current[a.face];
        if (wall) placeAccessory(wall, { id: a.id, staged: false, kind: a.kind, face: a.face, x: a.x, y: a.y, z: SLAT_FACE_Z }, roomLength, roomWidth, renderScene);
      }
    }

    // Procedural 2x4 framing (sibling of wall groups so transparency
    // controller leaves it alone). Visibility is toggled live; we always
    // build it so toggling on doesn't trigger a full rebuild.
    framingMeshesRef.current = [];
    if (showHeight) {
      // Windows and doors (3D Designer layouts) get rough openings in the framing.
      const faceOrder: WallFace[] = ['front', 'right', 'back', 'left'];
      const framingOpenings: Partial<Record<WallFace, { x: number; y: number; width: number; height: number }[]>> = {};
      boardLayouts?.walls.forEach((wall, i) => {
        if (wall.openings?.length && faceOrder[i]) framingOpenings[faceOrder[i]] = wall.openings;
      });
      const framingGroup = buildRoomFraming(roomLength, roomWidth, roomHeight, framingOpenings, framingOmit, framingShape(boardLayouts ?? undefined));
      framingGroup.visible = showFraming;
      roomGroup.add(framingGroup);
      framingGroupRef.current = framingGroup;
    } else {
      framingGroupRef.current = null;
    }
    // The bare areas' own studs stand in for the framing while it is hidden.
    setBareStudsVisible(roomGroup, !showFraming);

    dimensionsGroupRef.current = addDimensionLines(roomGroup, roomLength, roomWidth, roomHeight, showHeight, length, width, height, ceilingPeak(boardLayouts ?? undefined, roomHeight));
    roomGroup.position.set(-roomLength / 2, 0, -roomWidth / 2);
    roomGroup.userData.roomRoot = true;

    // Floor plans: the other rooms, in the same coordinates (so each sits at its
    // offset from this room's front-left corner).
    contextBuiltRef.current = [];
    contextRootRef.current = null;
    const cache = contextCacheRef.current;
    const kept = new Set<string>();
    if (showHeight && showBoards && contextRooms?.length) {
      const root = new THREE.Group();
      root.name = 'context-rooms';
      for (const other of contextRooms) {
        const inputs = [other.layouts, other.props, other.accessories, other.framingOmit, other.length, other.width, other.height, other.offsetX, other.offsetZ, highlightFloor];
        const hit = cache.get(other.id);
        if (hit && hit.inputs.length === inputs.length && hit.inputs.every((v, i) => v === inputs[i])) {
          // As built last time: back into the scene, standing where it did.
          const built = hit.built;
          built.segments = contextWallSegments(other, roomLength, roomWidth);
          built.framing.visible = showFraming;
          root.add(built.group);
          contextBuiltRef.current.push(built);
        } else {
          root.add(buildContextRoom(other, roomLength, roomWidth));
          cache.set(other.id, { inputs, built: contextBuiltRef.current[contextBuiltRef.current.length - 1] });
        }
        kept.add(other.id);
      }
      roomGroup.add(root);
      contextRootRef.current = root;
    }
    // The yard round the house (lawn, driveway, landings, fence), in this
    // room's coordinates, under the room group so the export carries it —
    // hidden: it is for the render, never the view (shown only while the
    // export is taken, exportScene).
    yardRootRef.current = null;
    if (yard) {
      const yardRoot = buildYard(yardInRoom(yard, { x: roomOrigin?.x ?? 0, z: roomOrigin?.z ?? 0 }));
      yardRoot.visible = false;
      roomGroup.add(yardRoot);
      yardRootRef.current = yardRoot;
    }
    // The staging's yard props (trees, shrubs) likewise.
    roomGroup.traverse((o) => { if (o.name === 'staging-yard') o.visible = false; });
    // Rooms gone, or built afresh above: the old builds go with the old scene.
    cache.forEach((entry, id) => {
      if (kept.has(id) && contextBuiltRef.current.some((b) => b === entry.built)) return;
      disposablesOf(entry.built.group, pendingDisposeRef.current);
      cache.delete(id);
    });

    // Walls under a sloped ceiling are cut to their shape with clipping planes,
    // which three wants in world coordinates — so only now the rooms are placed.
    bakeRoomClipping(roomGroup);

    // The moving parts of doors and windows, in this room and the others. A door
    // on a shared wall has one key for both its copies; any other is keyed by
    // the room it is in as well, so the same wall id in two rooms is two doors.
    movingPartsRef.current = [];
    openerHandlesRef.current = [];
    // The doors the staging holds open (a car standing part way out through a
    // garage door): opened here, and closed again once nothing holds them —
    // unless the user has since opened or closed them by hand.
    const held = new Set(heldOpenRef.current ?? []);
    roomGroup.traverse((obj) => {
      if (!obj.userData.movement || typeof obj.userData.openerKey !== 'string') return;
      const raw: string = obj.userData.openerKey;
      let owner = roomKey ?? 'room';
      for (let a = obj.parent; a; a = a.parent) {
        if (a.name.startsWith('context-room-')) { owner = a.name.slice('context-room-'.length); break; }
      }
      const key = raw.startsWith('link:') ? raw : `${owner}|${raw}`;
      obj.userData.openerId = key;
      if (held.has(raw) && owner === (roomKey ?? 'room')) {
        if (!openersRef.current.has(key) || autoOpenedRef.current.has(key)) { openersRef.current.set(key, { current: 1, target: 1 }); autoOpenedRef.current.add(key); }
      } else if (autoOpenedRef.current.has(key)) {
        autoOpenedRef.current.delete(key);
        openersRef.current.set(key, { current: 0, target: 0 });
      }
      movingPartsRef.current.push({ part: obj, key });
      obj.traverse((c) => { if (c.userData.openerHandle) openerHandlesRef.current.push(c); });
      placeMovingPart(obj, easeInOutCubic(openersRef.current.get(key)?.current ?? 0));
    });

    // The staged props, for hiding them while a wall is edited.
    stagedPropsRef.current = [];
    roomGroup.traverse((o) => { if (o.userData.stagedPropId) stagedPropsRef.current.push(o); });

    // Cache world positions of every framing mesh AFTER the roomGroup
    // offset is applied. Studs/plates don't move once placed, so renderScene
    // can compute camera-distance fade cheaply by reading these cached coords.
    if (framingGroupRef.current) {
      roomGroup.updateMatrixWorld(true);
      framingGroupRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const wp = new THREE.Vector3();
          child.getWorldPosition(wp);
          framingMeshesRef.current.push({ mesh: child, worldPos: wp });
        }
      });
    }
  };

  /** One of the plan's other rooms: floor, walls, ceiling and framing, like the focus room's. */
  const buildContextRoom = (other: ContextRoom, focusLength: number, focusWidth: number): THREE.Group => {
    const group = new THREE.Group();
    group.name = `context-room-${other.id}`;
    group.position.set(other.offsetX, 0, other.offsetZ);
    group.userData.roomRoot = true;

    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(other.length + PLAN_WALL_GAP_FT, other.width + PLAN_WALL_GAP_FT),
      createFloorMaterial(highlightFloor, () => renderSceneRef.current()),
    );
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(other.length / 2, 0, other.width / 2);
    floor.receiveShadow = true;
    floor.userData.planFloor = true; // the render pipeline treats it as it does the room's floor
    group.add(floor);

    const walls: Partial<Record<WallFace, THREE.Group>> = {};
    const openings: Partial<Record<WallFace, { x: number; y: number; width: number; height: number }[]>> = {};
    other.layouts.walls.forEach((layout, i) => {
      const face = WALL_FACES[i];
      if (!face) return;
      const wall = buildWallMesh(layout, face, other.length, other.width, other.height, (x, z) => ceilingHeightAt(other.layouts, x, z, other.height), getRenderSettings().bareWallFill);
      // Not the focus room's wall — the render pipeline finds that one (for its
      // window reflections) by this tag.
      delete wall.userData.wallFace;
      group.add(wall);
      walls[face] = wall;
      if (layout.openings?.length) openings[face] = layout.openings;
    });
    let ceiling: THREE.Group | null = null;
    if (other.layouts.ceiling) {
      ceiling = buildCeilingMesh(other.layouts.ceiling, other.length, other.width, other.height, getRenderSettings().bareCeilingFill);
      delete ceiling.userData.wallFace;
      group.add(ceiling);
    } else if (other.layouts.ceilingPlanes?.length) {
      ceiling = buildCeilingPlanesMesh(other.layouts.ceilingPlanes, other.length, other.width, getRenderSettings().bareCeilingFill);
      ceiling.traverse((o) => { delete o.userData.wallFace; });
      group.add(ceiling);
    }

    if (other.props?.length) addStagedProps(group, walls, other.props, other.length, other.width, SLAT_FACE_Z, renderScene);
    for (const a of other.accessories ?? []) {
      const wall = walls[a.face];
      if (wall) placeAccessory(wall, { id: a.id, staged: false, kind: a.kind, face: a.face, x: a.x, y: a.y, z: SLAT_FACE_Z }, other.length, other.width, renderScene);
    }

    const framing = buildRoomFraming(other.length, other.width, other.height, openings, other.framingOmit, framingShape(other.layouts));
    framing.name = 'context-framing';
    framing.children.forEach((child) => { child.name = `context-${child.name}`; });
    framing.visible = showFraming;
    group.add(framing);
    const framingMeshes: BuiltContextRoom['framingMeshes'] = [];
    framing.traverse((child) => {
      if (child instanceof THREE.Mesh) framingMeshes.push({ mesh: child, face: (child.userData.face as WallFace | 'ceiling') || 'front' });
    });

    contextBuiltRef.current.push({
      id: other.id,
      layouts: other.layouts,
      height: other.height,
      length: other.length,
      width: other.width,
      group, walls, ceiling, floor, framing, framingMeshes,
      segments: contextWallSegments(other, focusLength, focusWidth),
      clear: { front: 1, right: 1, back: 1, left: 1 },
      opacity: { front: 1, right: 1, back: 1, left: 1 },
    });
    return group;
  };

  // Live toggle: flip framing visibility without rebuilding the room.
  useEffect(() => {
    // The bare areas' own studs stand in for the framing while it is hidden (bareFill.ts).
    const roomRoot = framingGroupRef.current?.parent;
    if (roomRoot) setBareStudsVisible(roomRoot, !showFraming);
    for (const room of contextBuiltRef.current) { room.framing.visible = showFraming; setBareStudsVisible(room.group, !showFraming); }
    if (framingGroupRef.current) {
      framingGroupRef.current.visible = showFraming;
      renderScene();
    }
    // Notify the parent — the 3D Designer uses this to toggle its wall-
    // perimeter outline overlay (visible only when framing is hidden).
    onFramingToggle?.(showFraming);
  }, [showFraming, onFramingToggle]);


  // Live re-render when the dev panel adjusts framing settings.
  useEffect(() => {
    return onFramingSettingsChange(() => {
      renderScene();
    });
  }, []);

  // Enter/Exit Room toggle — smooth animated camera transition
  // Uses direct camera position blending to avoid state timing issues
  const toggleInsideRoom = useCallback(() => {
    if (isAnimating || !cameraRef.current) return;
    const entering = !isInsideRoomRef.current;

    setIsAnimating(true);

    const startRotation = { ...rotationRef.current };
    const startDistance = orbitDistanceRef.current;
    // toggleInsideRoom is only callable outside edit mode, so cameraRef is the
    // perspective camera here. The ortho camera lives only in lock state.
    const startFov = (cameraRef.current as THREE.PerspectiveCamera).fov;

    if (entering) {
      outsideDistanceRef.current = orbitDistanceRef.current;
    }

    const targetDistance = entering ? 0 : outsideDistanceRef.current;
    const targetRotation = entering ? { x: 0, y: 0 } : { ...INITIAL_ROTATION };
    const targetFov = entering ? 75 : DEFAULT_FOV;
    // Leaving from a spot the eye was moved to: drift back to the room's centre
    // on the way out, so the next entry starts there.
    const startPivot = pivotOffsetRef.current.clone();
    // Switch camera mode at the halfway point
    const switchAt = 0.5;

    const duration = 1000;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5
        ? 4 * progress * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      // Interpolate rotation (shortest angular path so accumulated revolutions
      // don't unwind through the transition) and orbit distance.
      rotationRef.current = {
        x: startRotation.x + shortestAngleDelta(startRotation.x, targetRotation.x) * eased,
        y: startRotation.y + shortestAngleDelta(startRotation.y, targetRotation.y) * eased,
      };
      orbitDistanceRef.current = startDistance + (targetDistance - startDistance) * eased;
      pivotOffsetRef.current.copy(startPivot).multiplyScalar(1 - eased);

      if (cameraRef.current && !((cameraRef.current as THREE.OrthographicCamera).isOrthographicCamera)) {
        const persp = cameraRef.current as THREE.PerspectiveCamera;
        persp.fov = startFov + (targetFov - startFov) * eased;
        persp.updateProjectionMatrix();
      }

      // Switch mode at halfway point
      if (entering && eased >= switchAt && !isInsideRoomRef.current) {
        isInsideRoomRef.current = true;
      } else if (!entering && eased >= switchAt && isInsideRoomRef.current) {
        isInsideRoomRef.current = false;
      }

      renderScene();

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isInsideRoomRef.current = entering;
        setIsInsideRoom(entering);
        orbitDistanceRef.current = targetDistance;
        rotationRef.current = { ...targetRotation };
        pivotOffsetRef.current.set(0, 0, 0);
        setIsAnimating(false);
        setHasRotated(false);
        animationFrameRef.current = null;
        renderScene();
      }
    };

    animate();
  }, [isAnimating]);

  // Inside the room: glide the eye to a spot on the floor (an x / z offset from
  // the room's centre). Eye height and the direction looked in stay as they are.
  const glideInsideTo = useCallback((target: THREE.Vector3, then?: () => void) => {
    if (isAnimatingRef.current) return;
    setIsAnimating(true);
    const start = pivotOffsetRef.current.clone();
    const startTime = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / INSIDE_GLIDE_MS, 1);
      pivotOffsetRef.current.lerpVectors(start, target, easeInOutCubic(progress));
      renderSceneRef.current();
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setHasRotated(true); // Reset View has somewhere to go back to
        animationFrameRef.current = null;
        then?.();
      }
    };
    animate();
  }, []);

  // Double-click the floor from inside the room → move there. The floor is the
  // plane y = 0 within the room's footprint; a ray that leaves the footprint
  // first has hit a wall, which is a click for the wall, not a move.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const raycaster = new THREE.Raycaster();
    const onDoubleClick = (e: MouseEvent) => {
      if (isLockedRef.current || isAnimatingRef.current) return;
      const cam = cameraRef.current;
      const canvas = rendererRef.current?.domElement;
      if (!cam || !canvas || e.target !== canvas) return;
      const rect = container.getBoundingClientRect();
      raycaster.setFromCamera(new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      ), cam);
      const { origin, direction } = raycaster.ray;
      if (direction.y > -1e-6) return; // looking level or up: no floor under the pointer
      const t = -origin.y / direction.y;
      const x = origin.x + direction.x * t;
      const z = origin.z + direction.z * t;
      const halfX = length / 2;
      const halfZ = width / 2;
      if (!isInsideRoomRef.current) {
        // From the orbit view, a double-click on any room's floor is for the host
        // (the designer opens its floor planner). The click that began it may
        // have asked to focus that room; that waits no longer.
        const onFloor = (Math.abs(x) <= halfX && Math.abs(z) <= halfZ)
          || !!contextRoomAt(contextRooms ?? [], { x, z }, length, width);
        if (onFloor && onFloorDoubleClick) {
          if (focusClickTimerRef.current !== null) { window.clearTimeout(focusClickTimerRef.current); focusClickTimerRef.current = null; }
          e.preventDefault();
          onFloorDoubleClick();
        }
        return;
      }
      // Stop short of the walls (in a very small room, half way to them).
      const reach = (half: number) => Math.max(0, half - Math.min(INSIDE_WALL_CLEARANCE_FT, half / 2));
      const clampTo = (v: number, centre: number, half: number) => centre + Math.max(-reach(half), Math.min(reach(half), v - centre));
      if (Math.abs(x) > halfX || Math.abs(z) > halfZ) {
        // The floor of another room of the plan — only if it can be seen into, which
        // is to say nothing (no wall, no door) stands between the eye and that spot.
        const other = contextRoomAt(contextRooms ?? [], { x, z }, length, width);
        if (!other) return;
        const blockers: THREE.Object3D[] = [
          ...Object.values(wallGroupsRef.current).filter((g): g is THREE.Group => !!g),
          ...contextBuiltRef.current.flatMap((c) => Object.values(c.walls).filter((g): g is THREE.Group => !!g)),
        ];
        const ray = new THREE.Raycaster(origin.clone(), direction.clone().normalize(), 0, t * direction.length() - 0.05);
        if (ray.intersectObjects(blockers, true).length > 0) return;
        // …and a person can walk there: nothing in the way at knee height either
        // (seen through a window is not a way through).
        const from = new THREE.Vector3(cam.position.x, 1.5, cam.position.z);
        const toward = new THREE.Vector3(x - from.x, 0, z - from.z);
        const walk = new THREE.Raycaster(from, toward.clone().normalize(), 0, Math.max(0, toward.length() - 0.05));
        if (walk.intersectObjects(blockers, true).length > 0) return;
        const f = contextFootprint(other, length, width);
        e.preventDefault();
        glideInsideTo(
          new THREE.Vector3(clampTo(x, (f.x0 + f.x1) / 2, other.length / 2), 0, clampTo(z, (f.z0 + f.z1) / 2, other.width / 2)),
          () => onRoomFocus?.(other.id),
        );
        return;
      }
      e.preventDefault();
      glideInsideTo(new THREE.Vector3(clampTo(x, 0, halfX), 0, clampTo(z, 0, halfZ)));
    };
    container.addEventListener('dblclick', onDoubleClick);
    return () => container.removeEventListener('dblclick', onDoubleClick);
  }, [length, width, glideInsideTo, contextRooms, onFloorDoubleClick, onRoomFocus]);

  // A wall's height at a point along it, and its highest point — the room
  // height, or more under a sloped ceiling (the wall's outline in its layout).
  const wallTopAt = useCallback((face: WallFace, sx: number): number => {
    const outline = boardLayoutsRef.current?.walls[WALL_FACES.indexOf(face)]?.outline;
    return outline ? outlineTopAt(outline, sx) : height;
  }, [height]);
  const wallTopOf = useCallback((face: SurfaceFace): number => {
    if (isCeilingFace(face)) return height;
    const wall = boardLayoutsRef.current?.walls[WALL_FACES.indexOf(face as WallFace)];
    return wall?.outline ? Math.max(...wall.outline.map((p) => p[1])) : (wall?.wallHeight ?? height);
  }, [height]);

  // A profiled ceiling's plane behind a face name, from the layouts drawn.
  const planeFor = useCallback((face: SurfaceFace | null): CeilingPlaneLayout | null => {
    if (!face || !isCeilingFace(face) || face === 'ceiling') return null;
    return boardLayoutsRef.current?.ceilingPlanes?.find((p) => p.id === face) ?? null;
  }, []);
  // A point of a plane's surface (feet along the ridge, up the slope) in world
  // coordinates: through the plane's matrix, then the room's offset from the origin.
  const planePointToWorld = useCallback((plane: CeilingPlaneLayout, sx: number, sy: number): THREE.Vector3 => {
    const m = plane.plane.matrix;
    return new THREE.Vector3(
      m[0] * sx + m[4] * sy + m[12] - length / 2,
      m[1] * sx + m[5] * sy + m[13],
      m[2] * sx + m[6] * sy + m[14] - width / 2,
    );
  }, [length, width]);

  // Build an orthographic camera positioned head-on to the given wall face. The
  // frustum is sized to fit the wall + ~15% margin (room for handles outside
  // the wall edge), respecting the container's aspect ratio so the wall renders
  // without stretch. Used by the edit-mode camera swap.
  /**
   * `keepUp` — a screen-up direction (world) to keep, for a ceiling plane: the
   * view is built with that direction still reading up the screen (projected
   * onto the plane) rather than the plane's own slope. Moving between the
   * planes of one ceiling keeps the picture the same way round.
   */
  const buildOrthoForLockedFace = useCallback((face: SurfaceFace, keepUp?: THREE.Vector3): THREE.OrthographicCamera | null => {
    const container = containerRef.current;
    if (!container) return null;
    // On-screen extents of the surface: a wall is (its run) × its height; the
    // ceiling is length × width, seen from below; a ceiling plane its true size.
    const lockedPlane = planeFor(face);
    const wallW = lockedPlane ? lockedPlane.plane.width : (face === 'left' || face === 'right') ? width : length;
    const wallH = lockedPlane ? lockedPlane.plane.height : face === 'ceiling' ? width : wallTopOf(face);
    const margin = 1.15; // 15% breathing room around the wall for handles
    const wallWP = wallW * margin;
    const wallHP = wallH * margin;
    // Fit the surface into the view MINUS the bands reserved top and bottom for
    // the host's floating controls, and centre it in what is left. Scale is
    // uniform (pixels per foot), so the surface is never stretched.
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const insetTop = Math.max(0, Math.min(editInsetTop, ch * 0.4));
    const insetBottom = Math.max(0, Math.min(editInsetBottom, ch * 0.2));
    const availH = Math.max(1, ch - insetTop - insetBottom);
    // Apply persisted user zoom (Ctrl+scroll).
    const pxPerFt = Math.min(cw / wallWP, availH / wallHP) * orthoZoomRef.current;
    const halfW = cw / (2 * pxPerFt);
    const halfH = ch / (2 * pxPerFt);
    // The camera aims at the surface's centre. Raising the frustum (in feet)
    // draws that centre BELOW the middle of the view — by half the difference
    // between the two bands, which is the middle of the area left between them.
    const lift = (insetTop - insetBottom) / 2 / pxPerFt;

    const ortho = new THREE.OrthographicCamera(-halfW, halfW, halfH + lift, -halfH + lift, 0.1, 200);

    // Position the ortho camera on the OPPOSITE side of the room from the
    // locked wall, looking straight at the wall's center. The actual distance
    // doesn't change scale (ortho is parallel projection), but it needs to be
    // far enough that the room is in front of the camera (between near and
    // far planes) so the meshes render.
    const D = 50;
    let pos: THREE.Vector3, target: THREE.Vector3;
    const up = new THREE.Vector3(0, 1, 0);
    const plane = planeFor(face);
    if (plane) {
      // A ceiling plane: from inside the room along its normal, the slope
      // reading up the screen and the ridge direction across it — and framed
      // on the WHOLE ceiling, so the other planes stay in view (foreshortened)
      // and can be clicked to move to.
      target = planePointToWorld(plane, plane.plane.width / 2, plane.plane.height / 2);
      const n = new THREE.Vector3(...plane.plane.normal);
      pos = target.clone().addScaledVector(n, D);
      up.set(...plane.plane.v);
      const keep = keepUp ?? planeUpRef.current;
      if (keep) {
        // The direction asked for, laid onto this plane. (A plane's slope on the
        // far side of the ridge runs the other way: using it would turn the
        // picture upside down.)
        const kept = keep.clone().addScaledVector(n, -keep.dot(n));
        if (kept.lengthSq() > 1e-6) up.copy(kept.normalize());
      }
      ortho.position.copy(pos);
      ortho.up.copy(up);
      ortho.lookAt(target);
      ortho.updateMatrixWorld();
      const box = new THREE.Box2();
      const v = new THREE.Vector3();
      for (const p of boardLayoutsRef.current?.ceilingPlanes ?? []) {
        for (const [sx, sy] of [[0, 0], [p.plane.width, 0], [0, p.plane.height], [p.plane.width, p.plane.height]]) {
          v.copy(planePointToWorld(p, sx, sy)).applyMatrix4(ortho.matrixWorldInverse);
          box.expandByPoint(new THREE.Vector2(v.x, v.y));
        }
      }
      const extent = box.getSize(new THREE.Vector2());
      const centre = box.getCenter(new THREE.Vector2());
      const fit = Math.min(cw / (Math.max(extent.x, 1) * margin), availH / (Math.max(extent.y, 1) * margin)) * orthoZoomRef.current;
      const hw = cw / (2 * fit);
      const hh = ch / (2 * fit);
      const raise = (insetTop - insetBottom) / 2 / fit;
      ortho.left = centre.x - hw;
      ortho.right = centre.x + hw;
      ortho.top = centre.y + hh + raise;
      ortho.bottom = centre.y - hh + raise;
      ortho.updateProjectionMatrix();
      return ortho;
    }
    switch (face as WallFace | 'ceiling') {
      case 'ceiling':
        // From below, looking straight up. With +Z as "up" the ceiling reads
        // +X → screen-right and +Z → screen-up: the same way round as its
        // surface coordinates, and the same way the perspective lock arrives.
        pos = new THREE.Vector3(0, -D, 0);
        target = new THREE.Vector3(0, height, 0);
        up.set(0, 0, 1);
        break;
      case 'front':
        pos = new THREE.Vector3(0, height / 2, D);
        target = new THREE.Vector3(0, height / 2, -width / 2);
        break;
      case 'back':
        pos = new THREE.Vector3(0, height / 2, -D);
        target = new THREE.Vector3(0, height / 2, width / 2);
        break;
      case 'left':
        pos = new THREE.Vector3(D, height / 2, 0);
        target = new THREE.Vector3(-length / 2, height / 2, 0);
        break;
      case 'right':
        pos = new THREE.Vector3(-D, height / 2, 0);
        target = new THREE.Vector3(length / 2, height / 2, 0);
        break;
    }
    ortho.position.copy(pos);
    ortho.up.copy(up);
    ortho.lookAt(target);
    ortho.updateMatrixWorld();
    return ortho;
  }, [length, width, height, editInsetTop, editInsetBottom, planeFor, planePointToWorld]);

  // Project the currently-locked surface to a surface-local-feet -> container-px
  // affine and emit it via onProjectionChange. Drives the 3D Designer's handle
  // overlay, for walls and the ceiling alike.
  const recomputeProjection = useCallback(() => {
    if (!onProjectionChange) return;
    const face = selectedFaceRef.current;
    const container = containerRef.current;
    const cam = cameraRef.current;
    if (!isLockedRef.current || !face || !container || !cam) {
      onProjectionChange(null);
      return;
    }
    // Sync renderer + camera projection to the container's CURRENT size. For
    // perspective: update aspect + projectionMatrix. For ortho: the frustum is
    // intrinsic to the camera and was sized to the container at lock time, so
    // we don't touch it here (a container-size change during ortho edit-mode is
    // currently rare since the side panel is always reserved).
    const renderer = rendererRef.current;
    const cw = container.clientWidth;
    const ch = container.clientHeight;
    const isOrtho = (cam as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (cw > 0 && ch > 0 && renderer && !isOrtho) {
      const persp = cam as THREE.PerspectiveCamera;
      const newAspect = cw / ch;
      const aspectChanged = Math.abs(persp.aspect - newAspect) > 1e-4;
      const canvas = renderer.domElement;
      const sizeChanged = canvas.clientWidth !== cw || canvas.clientHeight !== ch;
      if (aspectChanged || sizeChanged) {
        renderer.setSize(cw, ch);
        persp.aspect = newAspect;
        persp.updateProjectionMatrix();
        renderScene();
      }
    }
    const canvas = renderer?.domElement;
    const canvasW = canvas?.clientWidth || container.clientWidth;
    const canvasH = canvas?.clientHeight || container.clientHeight;
    cam.updateMatrixWorld();
    const plane = planeFor(face);
    const surfaceW = plane ? plane.plane.width : (face === 'left' || face === 'right') ? width : length;
    const surfaceH = plane ? plane.plane.height : face === 'ceiling' ? width : wallTopOf(face);
    // World coords for the wall point. The roomGroup is translated by
    // (-length/2, 0, -width/2) (see roomGroup.position.set in buildRoom), so
    // each face's canonical wall coords land at these offset world positions.
    const wallPointToWorld = (sx: number, sy: number): THREE.Vector3 => {
      if (plane) return planePointToWorld(plane, sx, sy);
      switch (face as WallFace | 'ceiling') {
        case 'front': return new THREE.Vector3(sx - length / 2,           sy, -width / 2);
        case 'back':  return new THREE.Vector3(length / 2 - sx,           sy,  width / 2);
        case 'left':  return new THREE.Vector3(-length / 2,               sy, sx - width / 2);
        case 'right': return new THREE.Vector3( length / 2,               sy, width / 2 - sx);
        // Ceiling: surface x runs along the room's length, y along its width.
        case 'ceiling': return new THREE.Vector3(sx - length / 2,     height, sy - width / 2);
      }
    };
    // Convert NDC to pixels using the canvas's actual displayed size — that's
    // what the renderer maps NDC to. If the canvas's CSS size differs from
    // container.clientWidth (e.g., because setSize fired with a stale size),
    // the canvas wins for visual placement and so should our projection.
    const pxW = canvasW || container.clientWidth;
    const pxH = canvasH || container.clientHeight;
    const worldToPx = (p: THREE.Vector3) => {
      const ndc = p.clone().project(cam);
      return {
        x: (ndc.x + 1) * 0.5 * pxW,
        y: (-ndc.y + 1) * 0.5 * pxH,
      };
    };
    const bl = worldToPx(wallPointToWorld(0, 0));
    const br = worldToPx(wallPointToWorld(surfaceW, 0));
    const tl = worldToPx(wallPointToWorld(0, surfaceH));
    onProjectionChange({
      face,
      originPx: bl,
      axisX: { x: (br.x - bl.x) / surfaceW, y: (br.y - bl.y) / surfaceW },
      axisY: { x: (tl.x - bl.x) / surfaceH, y: (tl.y - bl.y) / surfaceH },
    });
  }, [length, width, onProjectionChange, planeFor, planePointToWorld, wallTopOf]);

  // Mirror recomputeProjection into a ref so resize/fullscreen handlers (in
  // setup useEffects with stable deps) can call the latest version without
  // triggering effect re-runs.
  const recomputeProjectionRef = useRef<() => void>(() => {});
  useEffect(() => { recomputeProjectionRef.current = recomputeProjection; }, [recomputeProjection]);

  // World-space centre of a surface (the room is centred on the origin in X/Z
  // and sits on y = 0). Analytic rather than measured from the meshes, so it is
  // right even when the surface is only partly covered — or completely bare.
  const faceCenterWorld = useCallback((face: SurfaceFace): THREE.Vector3 => {
    const plane = planeFor(face);
    if (plane) return planePointToWorld(plane, plane.plane.width / 2, plane.plane.height / 2);
    switch (face as WallFace | 'ceiling') {
      case 'front': return new THREE.Vector3(0, wallTopOf(face) / 2, -width / 2);
      case 'back': return new THREE.Vector3(0, wallTopOf(face) / 2, width / 2);
      case 'left': return new THREE.Vector3(-length / 2, wallTopOf(face) / 2, 0);
      case 'right': return new THREE.Vector3(length / 2, wallTopOf(face) / 2, 0);
      case 'ceiling': return new THREE.Vector3(0, height, 0);
    }
    return new THREE.Vector3(0, height, 0);
  }, [length, width, height, planeFor, planePointToWorld, wallTopOf]);

  // ── Edit mode: click a wall/ceiling → rotate head-on and lock ──
  const lockToFace = useCallback((face: SurfaceFace) => {
    if (!wallGroupsRef.current[face] || isAnimating) return;
    // Under a profiled ceiling "the ceiling" is its planes; each is locked on its own.
    if (face === 'ceiling' && boardLayoutsRef.current?.ceilingPlanes?.length) return;
    if (isLockedRef.current && selectedFaceRef.current === face) return; // already there

    // From one ceiling plane to another: the editing camera glides straight to
    // square on the new plane — the whole ceiling stays in view throughout, no
    // trip out to the perspective view and back. The picture stays the same way
    // round: the new view keeps the current screen-up, so the move is a tilt
    // over the ridge, never a turn-about.
    const current = cameraRef.current;
    if (isLockedRef.current && planeFor(selectedFaceRef.current) && planeFor(face) && current instanceof THREE.OrthographicCamera) {
      const screenUp = new THREE.Vector3(0, 1, 0).applyQuaternion(current.quaternion);
      const to = buildOrthoForLockedFace(face, screenUp);
      if (!to) return;
      planeUpRef.current = screenUp;
      selectedFaceRef.current = face;
      setSelectedFace(face);
      onFaceLock?.(face);
      onProjectionChange?.(null);
      setIsAnimating(true);
      const p0 = current.position.clone(), q0 = current.quaternion.clone();
      const f0 = { left: current.left, right: current.right, top: current.top, bottom: current.bottom };
      const f1 = { left: to.left, right: to.right, top: to.top, bottom: to.bottom };
      const startTime = Date.now();
      const duration = 450;
      const step = () => {
        const t = easeInOutCubic(Math.min(1, (Date.now() - startTime) / duration));
        current.position.lerpVectors(p0, to.position, t);
        current.quaternion.slerpQuaternions(q0, to.quaternion, t);
        current.left = f0.left + (f1.left - f0.left) * t;
        current.right = f0.right + (f1.right - f0.right) * t;
        current.top = f0.top + (f1.top - f0.top) * t;
        current.bottom = f0.bottom + (f1.bottom - f0.bottom) * t;
        current.updateProjectionMatrix();
        current.updateMatrixWorld();
        renderScene();
        if (t < 1) { animationFrameRef.current = requestAnimationFrame(step); return; }
        animationFrameRef.current = null;
        cameraRef.current = to;
        setIsAnimating(false);
        renderScene();
        recomputeProjection();
      };
      step();
      return;
    }
    if (isLockedRef.current && selectedFaceRef.current === face) return; // already there
    // Already locked on another surface: the active camera is the ortho editor
    // camera, which has nothing to animate. Bring the parked perspective camera
    // back and turn from where we are to the new surface.
    if (perspectiveCameraRef.current) {
      cameraRef.current = perspectiveCameraRef.current;
      perspectiveCameraRef.current = null;
    }
    // From here cameraRef is the perspective camera; we swap to ortho at the
    // END of the animation (see below).
    const cam = cameraRef.current as THREE.PerspectiveCamera | null;
    if (!cam || !(cam as THREE.PerspectiveCamera).isPerspectiveCamera) return;

    // Clicked from INSIDE the room: carry on from the same view, re-expressed
    // as an orbit drawn right in to the room's centre (looking out along d is
    // orbiting at -d), so the turn to the wall and the pull back out to the
    // editing distance are one move. Leaving edit mode then lands outside.
    const fromInside = isInsideRoomRef.current;
    if (!isLockedRef.current) {
      // Leaving edit mode goes back to where the lock began: inside the room
      // (at the same spot), or the orbit view.
      lockedFromInsideRef.current = fromInside;
      if (fromInside) preLockEyeRef.current = pivotOffsetRef.current.clone();
    }
    if (fromInside) {
      isInsideRoomRef.current = false;
      setIsInsideRoom(false);
      rotationRef.current = { x: -rotationRef.current.x, y: rotationRef.current.y + Math.PI };
      orbitDistanceRef.current = 0.01;
      // Start the turn at eye height (the orbit pivot is half way up the room).
      pivotOffsetRef.current.y = eyeY() - roomCenterRef.current.y;
    }
    const startFov = cam.fov;
    const targetFov = fromInside ? DEFAULT_FOV : cam.fov;

    // A wall or the flat ceiling is looked at from the room's centre; a ceiling
    // plane along its own normal, orbiting its centre (the pivot moves there).
    const lockedPlane = planeFor(face);
    const camDir = lockedPlane
      ? new THREE.Vector3(...lockedPlane.plane.normal)
      : new THREE.Vector3().subVectors(roomCenterRef.current, faceCenterWorld(face));
    if (camDir.lengthSq() < 1e-6) return;
    camDir.normalize();
    const targetPivot = lockedPlane ? faceCenterWorld(face).sub(roomCenterRef.current) : new THREE.Vector3();

    // Tight clamp so the ceiling/floor lock reads essentially dead-on, while a
    // hair off true vertical avoids the lookAt gimbal (up-vector parallel).
    const clampX = (v: number) => Math.max(-Math.PI / 2 + 0.03, Math.min(Math.PI / 2 - 0.03, v));
    const target = {
      x: clampX(Math.asin(Math.max(-1, Math.min(1, camDir.y)))),
      y: Math.atan2(camDir.x, camDir.z),
    };

    // Zoom so the selected wall fills the viewport (head-on fit). wallW/wallH are
    // the wall's on-screen extents; halfDepth is its offset from room center along
    // the view axis (camera-to-wall distance = orbitDistance + halfDepth).
    let wallW: number, wallH: number, halfDepth: number;
    if (lockedPlane) { wallW = lockedPlane.plane.width; wallH = (boardLayoutsRef.current?.ceilingPlanes ?? []).reduce((t, p) => t + p.plane.height, 0); halfDepth = 0; }
    else if (face === 'left' || face === 'right') { wallW = width; wallH = wallTopOf(face); halfDepth = length / 2; }
    else if (face === 'ceiling') { wallW = length; wallH = width; halfDepth = height / 2; }
    else { wallW = length; wallH = wallTopOf(face); halfDepth = width / 2; }
    const vFov = (targetFov * Math.PI) / 180;
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * cam.aspect);
    const fitDist = Math.max((wallH / 2) / Math.tan(vFov / 2), (wallW / 2) / Math.tan(hFov / 2)) / 0.92;
    const targetDistance = Math.max(2, fitDist - halfDepth);

    if (!isLockedRef.current) {
      preLockDistanceRef.current = fromInside ? outsideDistanceRef.current : orbitDistanceRef.current;
      preLockRotationRef.current = { ...rotationRef.current };
    }
    isLockedRef.current = true;
    selectedFaceRef.current = face;
    setSelectedFace(face);
    onFaceLock?.(face);
    // Clear any stale projection — the overlay should disappear during the lock
    // transition and reappear with fresh coords once we settle head-on.
    onProjectionChange?.(null);
    // Hide the floor ONLY when the ceiling is selected, so it doesn't occlude the
    // ceiling view. Tied strictly to selection (restored on exit / other faces),
    // never to rotation.
    if (floorMeshRef.current) floorMeshRef.current.visible = !isCeilingFace(face);
    setIsAnimating(true);

    const startRotation = { ...rotationRef.current };
    const startDistance = orbitDistanceRef.current;
    // Locked from a spot the eye had been moved to: the pivot returns to the
    // room's centre during the turn (the editing view is framed on the room) —
    // or moves to a ceiling plane's centre, to look at it square.
    const startPivot = pivotOffsetRef.current.clone();
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const eased = easeInOutCubic(progress);
      rotationRef.current = {
        x: startRotation.x + shortestAngleDelta(startRotation.x, target.x) * eased,
        y: startRotation.y + shortestAngleDelta(startRotation.y, target.y) * eased,
      };
      orbitDistanceRef.current = startDistance + (targetDistance - startDistance) * eased;
      pivotOffsetRef.current.lerpVectors(startPivot, targetPivot, eased);
      if (targetFov !== startFov && cameraRef.current instanceof THREE.PerspectiveCamera) {
        cameraRef.current.fov = startFov + (targetFov - startFov) * eased;
        cameraFovRef.current = cameraRef.current.fov;
        cameraRef.current.updateProjectionMatrix();
      }
      renderScene();
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Land on the target (normalised so we don't carry accumulated turns).
        rotationRef.current = {
          x: startRotation.x + shortestAngleDelta(startRotation.x, target.x),
          y: startRotation.y + shortestAngleDelta(startRotation.y, target.y),
        };
        orbitDistanceRef.current = targetDistance;
        setIsAnimating(false);
        animationFrameRef.current = null;
        // Wall opacity eases a little on every frame, and frames only happen
        // while something is moving — so on a slow machine the turn can end
        // before the easing does, leaving the locked wall part see-through.
        // Land exactly on the edit-mode spotlight before the final frame.
        currentOpacityRef.current = {
          front: face === 'front' ? 1 : 0,
          back: face === 'back' ? 1 : 0,
          left: face === 'left' ? 1 : 0,
          right: face === 'right' ? 1 : 0,
          ceiling: isCeilingFace(face) ? 1 : 0,
        };
        renderScene();
        // Swap to an orthographic camera for editing — flat projection, no FOV
        // sensitivity, no perspective distortion. Park the perspective camera
        // for exitEditMode to restore. Reset the user zoom for each new lock.
        if (cameraRef.current instanceof THREE.PerspectiveCamera) {
          perspectiveCameraRef.current = cameraRef.current;
          orthoZoomRef.current = 1.0;
          planeUpRef.current = null; // a fresh lock reads the plane its own way up
          const ortho = buildOrthoForLockedFace(face);
          if (ortho) {
            cameraRef.current = ortho;
            renderScene();
          }
        }
        recomputeProjection();
      }
    };
    animate();
  }, [isAnimating, onFaceLock, onProjectionChange, recomputeProjection, buildOrthoForLockedFace, faceCenterWorld, planeFor, wallTopOf, length, width, height]);

  // Leave edit mode the way it was entered, in reverse: from head-on at the
  // editing distance, turn and pull back to the view the lock started from,
  // the other walls easing back in on the way.
  const exitEditMode = useCallback(() => {
    // A lock still turning in is simply turned back from where it has got to.
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    lastLockedFaceRef.current = selectedFaceRef.current;
    isLockedRef.current = false;
    selectedFaceRef.current = null;
    setSelectedFace(null);
    onFaceLock?.(null);
    onProjectionChange?.(null);
    // Back to the parked perspective camera (we swapped to ortho on lock). The
    // orbit angle and distance are still the head-on ones the lock settled on,
    // so it picks up looking straight at the surface.
    if (perspectiveCameraRef.current) {
      cameraRef.current = perspectiveCameraRef.current;
      perspectiveCameraRef.current = null;
    }
    orthoZoomRef.current = 1.0;
    planeUpRef.current = null;
    if (floorMeshRef.current) floorMeshRef.current.visible = true; // restore floor

    // Locked from inside the room: go back in — to the spot the eye was at, eye
    // height, looking straight at the surface that was being edited.
    const face = lastLockedFaceRef.current;
    if (lockedFromInsideRef.current && face) {
      lockedFromInsideRef.current = false;
      preLockRotationRef.current = null;
      const eye = (preLockEyeRef.current ?? new THREE.Vector3()).clone();
      eye.y = eyeY() - roomCenterRef.current.y;
      const eyeWorld = roomCenterRef.current.clone().add(eye);
      const look = new THREE.Vector3().subVectors(faceCenterWorld(face), eyeWorld);
      if (!isCeilingFace(face)) look.y = 0; // a wall: look at it level
      look.normalize();
      const clampX = (v: number) => Math.max(-Math.PI / 2 + 0.25, Math.min(Math.PI / 2 - 0.25, v));
      const inside = { x: clampX(Math.asin(Math.max(-1, Math.min(1, look.y)))), y: Math.atan2(look.x, look.z) };
      // The same view as an orbit drawn right in on the eye (as lockToFace does, reversed).
      const asOrbit = { x: -inside.x, y: inside.y + Math.PI };
      const startRotation = { ...rotationRef.current };
      const startDistance = orbitDistanceRef.current;
      const startPivot = pivotOffsetRef.current.clone();
      const cam = cameraRef.current as THREE.PerspectiveCamera;
      const startFov = cam.fov;
      const landed = {
        x: startRotation.x + shortestAngleDelta(startRotation.x, asOrbit.x),
        y: startRotation.y + shortestAngleDelta(startRotation.y, asOrbit.y),
      };
      outsideDistanceRef.current = preLockDistanceRef.current > 0 ? preLockDistanceRef.current : outsideDistanceRef.current;
      setIsAnimating(true);
      const duration = 700;
      const startTime = Date.now();
      const animate = () => {
        const progress = Math.min((Date.now() - startTime) / duration, 1);
        const eased = easeInOutCubic(progress);
        rotationRef.current = {
          x: startRotation.x + (landed.x - startRotation.x) * eased,
          y: startRotation.y + (landed.y - startRotation.y) * eased,
        };
        orbitDistanceRef.current = startDistance + (0.01 - startDistance) * eased;
        pivotOffsetRef.current.lerpVectors(startPivot, eye, eased);
        cam.fov = startFov + (INSIDE_FOV - startFov) * eased;
        cameraFovRef.current = cam.fov;
        cam.updateProjectionMatrix();
        if (progress < 1) {
          renderScene();
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          // Inside again.
          isInsideRoomRef.current = true;
          setIsInsideRoom(true);
          rotationRef.current = inside;
          orbitDistanceRef.current = 0;
          pivotOffsetRef.current.set(eye.x, 0, eye.z);
          animationFrameRef.current = null;
          setIsAnimating(false);
          setHasRotated(true);
          currentOpacityRef.current = { front: 1, back: 1, left: 1, right: 1, ceiling: 1 };
          renderScene();
        }
      };
      animate();
      return;
    }

    const startRotation = { ...rotationRef.current };
    const startDistance = orbitDistanceRef.current;
    const startPivot = pivotOffsetRef.current.clone();
    const target = preLockRotationRef.current ?? startRotation;
    const targetDistance = preLockDistanceRef.current > 0 ? preLockDistanceRef.current : startDistance;
    preLockRotationRef.current = null;
    const landed = {
      x: startRotation.x + shortestAngleDelta(startRotation.x, target.x),
      y: startRotation.y + shortestAngleDelta(startRotation.y, target.y),
    };

    setIsAnimating(true);
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const progress = Math.min((Date.now() - startTime) / duration, 1);
      const eased = easeInOutCubic(progress);
      rotationRef.current = {
        x: startRotation.x + (landed.x - startRotation.x) * eased,
        y: startRotation.y + (landed.y - startRotation.y) * eased,
      };
      orbitDistanceRef.current = startDistance + (targetDistance - startDistance) * eased;
      pivotOffsetRef.current.copy(startPivot).multiplyScalar(1 - eased);
      if (progress < 1) {
        renderScene();
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        rotationRef.current = landed;
        orbitDistanceRef.current = targetDistance;
        pivotOffsetRef.current.set(0, 0, 0);
        animationFrameRef.current = null;
        setIsAnimating(false);
        // The wall fade eases a little each frame; land it exactly on what this
        // angle shows, in case the turn outran it.
        currentOpacityRef.current = calculateWallOpacities(landed.y, landed.x);
        renderScene();
      }
    };
    animate();
  }, [onFaceLock, onProjectionChange, faceCenterWorld]);

  // The room as built, for rendering elsewhere (sceneExport.ts): this room and
  // the plan's others, the perspective camera's view (the parked one while a
  // wall is being edited), the lamps where they hang, and the look.
  const exportScene = useCallback(async (options: { panoramaFace?: WallFace; camera?: CameraPose } = {}): Promise<{ glb: ArrayBuffer; manifest: RenderManifest } | null> => {
    const roomGroup = roomGroupRef.current;
    const container = containerRef.current;
    if (!roomGroup || !container) return null;
    const cam = cameraRef.current instanceof THREE.PerspectiveCamera ? cameraRef.current : perspectiveCameraRef.current;
    if (!cam) return null;
    const roots: THREE.Object3D[] = [roomGroup, ...contextBuiltRef.current.map((r) => r.group)];
    // The dimension callouts are the viewer's, not the room's. (Hidden framing is left out by being hidden.)
    const exclude = [dimensionsGroupRef.current].filter((o): o is THREE.Group => !!o);
    // The render is of the whole room: whatever the view has faded out or
    // hidden (the walls between the camera and the room, when it is orbiting
    // outside) is brought back for the export, then put back as it was.
    const restoreWalls = showWholeRoom(roomGroup);
    // The props go out as their stand-ins, never as the previews drawn in
    // their place: the renderer swaps the stand-ins for its own models.
    const restoreProps = standInsForExport(roots);
    // The yard and the staging's yard props are hidden in the view and go out
    // with the export: shown for it, hidden again after.
    const yardParts: THREE.Object3D[] = [];
    for (const root of roots) root.traverse((o) => { if ((o.userData.yard && o.parent && !o.parent.userData.yard) || o.name === 'staging-yard') yardParts.push(o); });
    for (const o of yardParts) o.visible = true;
    let glb: ArrayBuffer;
    try {
      glb = await exportRoomGlb(roots, { exclude });
    } finally {
      for (const o of yardParts) o.visible = false;
      restoreProps();
      restoreWalls();
    }
    const s = getRenderSettings();
    const L = length, W = width, H = height;
    const lamps = lampPlacements(s, L, W, H,
      (x, z) => ceilingHeightAt(boardLayouts ?? undefined, x, z, H),
      (x, z) => ceilingNormalAt(boardLayouts ?? undefined, x, z));
    const offset = roomGroup.position;
    // The 360: the room's centre at eye height (as the renderer's corner
    // views), looking level at the middle of the wall it opens on.
    const eye = Math.min(5.5, H - 1.0);
    const [dx, dz] = towardWall(options.panoramaFace ?? 'front');
    const centre: [number, number, number] = [offset.x + L / 2, offset.y + eye, offset.z + W / 2];
    // A render is ALWAYS taken from inside the room (Rob, 2026-10-13). Inside
    // it, the camera is the user's; from anywhere else (orbiting outside, or
    // locked to a wall in edit mode, where the camera is orthographic) it is
    // the camera the host asked for (a corner view, the same one its preview
    // still was taken with — renderPose.ts), else the view Enter Room opens
    // on: the room's centre at eye height, level, straight at the back wall.
    const aspect = container.clientWidth / Math.max(1, container.clientHeight);
    const renderCamera = (): RenderManifest['camera'] => {
      const ortho = (cam as THREE.Camera as THREE.OrthographicCamera).isOrthographicCamera === true;
      if (isInsideRoomRef.current && !ortho) return cameraManifest(cam, aspect);
      if (options.camera) return { ...options.camera, aspect: 16 / 9 };
      const eye = roomCenterRef.current.clone().add(INSIDE_EYE_OFFSET);
      eye.y = eyeY();
      return { position: eye.toArray() as [number, number, number], target: [eye.x, eye.y, eye.z + 10], fov: INSIDE_FOV, aspect };
    };
    const manifest: RenderManifest = {
      version: 1,
      units: 'feet',
      room: { length: L, width: W, height: H, peak: ceilingPeak(boardLayouts ?? undefined, H) },
      camera: renderCamera(),
      lamps: lamps.map((p) => ({
        position: [p.lamp.x + offset.x, p.lamp.y + offset.y, p.lamp.z + offset.z],
        kelvin: s.interiorWarmth, intensity: s.interiorIntensity / Math.max(1, lamps.length), fixture: s.interiorFixtureStyle,
      })),
      ambient: s.ambientIntensity,
      look: { warmth: s.interiorWarmth, intensity: s.interiorIntensity, ambient: s.ambientIntensity, environment: s.environment, exposure: s.exposure, seamStrength: s.seamStrength },
      panorama: { position: centre, target: [centre[0] + dx * 5, centre[1], centre[2] + dz * 5], face: options.panoramaFace ?? 'front' },
    };
    return { glb, manifest };
  }, [length, width, height, boardLayouts]);

  // The camera a picture of the room is taken with (renderPose.ts): the
  // user's own view when they are inside the room, else a corner view that
  // shows the room's feature wall.
  const framing = useCallback((feature: WallFace, secondary?: WallFace): CameraPose | null => {
    if (!roomGroupRef.current) return null;
    const cam = cameraRef.current;
    const ortho = !!cam && (cam as THREE.Camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (isInsideRoomRef.current && cam && !ortho) {
      const m = cameraManifest(cam as THREE.PerspectiveCamera, 16 / 9);
      return { position: m.position, target: m.target, fov: m.fov };
    }
    return cornerPose({ length, width, height }, cornerFor(feature, secondary));
  }, [length, width, height]);

  // A still of the room as drawn, at any size and from any pose, with the
  // view put back as it was: the canvas is resized for one frame (drawing
  // buffer only — the CSS size is untouched), the pose applied through
  // `poseOverrideRef`, the frame drawn and read back in the same task (the
  // drawing buffer is not preserved), composited onto the scene's backdrop
  // colour (the pipeline clears to transparent) and encoded as a JPEG.
  const snapshot = useCallback(async (options: { width: number; height: number; pose?: CameraPose; quality?: number }) => {
    const renderer = rendererRef.current;
    const roomGroup = roomGroupRef.current;
    if (!renderer || !roomGroup || !containerRef.current || suspendedRef.current) return null;
    if (renderer.getContext().isContextLost()) return null;
    if (compilesRef.current.size) await Promise.all([...compilesRef.current]);
    const { width: w, height: h, pose, quality = 0.85 } = options;
    const cam = cameraRef.current instanceof THREE.PerspectiveCamera ? cameraRef.current : perspectiveCameraRef.current;
    if (!cam) return null;
    const prevCamera = cameraRef.current;
    const prevRatio = renderer.getPixelRatio();
    const prevFov = cam.fov;
    const prevAspect = cam.aspect;
    const prevOpacity = { ...currentOpacityRef.current };
    const restoreWalls = showWholeRoom(roomGroup);
    let dataUrl: string | null = null;
    let camera: RenderManifest['camera'] | null = null;
    try {
      cameraRef.current = cam;
      snapshotRef.current = true;
      poseOverrideRef.current = pose ?? null;
      renderer.setPixelRatio(1);
      renderer.setSize(w, h, false);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      renderSceneRef.current();
      if (!renderer.getContext().isContextLost()) {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#f9fafb';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(renderer.domElement, 0, 0, w, h);
          dataUrl = canvas.toDataURL('image/jpeg', quality);
        }
        camera = cameraManifest(cam, w / h);
      }
    } finally {
      snapshotRef.current = false;
      poseOverrideRef.current = null;
      restoreWalls();
      cam.fov = prevFov;
      cam.aspect = prevAspect;
      cam.updateProjectionMatrix();
      cameraRef.current = prevCamera;
      renderer.setPixelRatio(prevRatio);
      currentOpacityRef.current = prevOpacity;
      syncSizeRef.current();
    }
    return dataUrl && camera ? { dataUrl, camera } : null;
  }, []); // refs only: the latest renderScene and syncSize come through their refs

  // Imperative handle for hosts that drive the lock themselves (3D Designer).
  useEffect(() => {
    if (!apiRef) return;
    apiRef.current = { lockToFace, exitEditMode, exportScene, framing, snapshot };
    return () => { apiRef.current = null; };
  }, [apiRef, lockToFace, exitEditMode, exportScene, framing, snapshot]);

  // Restore a lock the host asked for (e.g. the wall being edited before a
  // reload). Once per mount, as soon as the scene — and so the wall groups — exist.
  const initialLockAppliedRef = useRef(false);
  useEffect(() => {
    if (!editable || !initialLockedFace || !sceneReady || initialLockAppliedRef.current) return;
    initialLockAppliedRef.current = true;
    if (wallGroupsRef.current[initialLockedFace]) lockToFace(initialLockedFace);
    else onFaceLock?.(null); // that surface isn't in this room — tell the host
  }, [editable, initialLockedFace, sceneReady, lockToFace, onFaceLock]);

  // ── Edit-mode clicks (3D Designer) ──
  // One handler for a click / tap on the canvas:
  //   - not locked → lock the surface under the pointer;
  //   - locked     → select the section under the pointer, or clear the
  //                  selection when the click lands on bare wall.
  // A click that ends an orbit drag is not a click (see dragMovedRef), and
  // clicks on the editor overlay (handles, chips, toolbar) never get here —
  // only the canvas itself counts.
  useEffect(() => {
    if (!editable) return;
    const container = containerRef.current;
    if (!container) return;
    const raycaster = new THREE.Raycaster();

    // The surface under the pointer, chosen by intersecting the ray with each
    // surface's rectangle (not its meshes — a bare wall must still be
    // clickable). Only surfaces the camera angle shows as solid count: you lock
    // the wall you SEE, never the see-through foreground wall (or ceiling) the
    // ray passes on its way there — so clicking through to the floor does
    // nothing. Of the solid ones the ray crosses, the nearest wins.
    const pickFace = (): { face: SurfaceFace; t: number } | null => {
      // (From inside the room every surface is solid.)
      const shown: TransparencyState = isInsideRoomRef.current
        ? { front: 1, back: 1, left: 1, right: 1, ceiling: 1 }
        : calculateWallOpacities(rotationRef.current.y, rotationRef.current.x);
      const { origin: o, direction: d } = raycaster.ray;
      const hx = length / 2;
      const hz = width / 2;
      const candidates: { face: SurfaceFace; t: number }[] = [];
      const cross = (face: SurfaceFace, axis: 'x' | 'y' | 'z', at: number) => {
        if (!wallGroupsRef.current[face]) return;
        if (Math.abs(d[axis]) < 1e-9) return;
        const t = (at - o[axis]) / d[axis];
        if (t <= 0) return;
        const px = o.x + d.x * t;
        const py = o.y + d.y * t;
        const pz = o.z + d.z * t;
        // A wall reaches up to its real top at that point (the rake under a slope).
        const top = axis === 'y' ? 0
          : face === 'front' ? wallTopAt('front', px + hx) : face === 'back' ? wallTopAt('back', hx - px)
            : face === 'left' ? wallTopAt('left', pz + hz) : wallTopAt('right', hz - pz);
        const inside = axis === 'z' ? Math.abs(px) <= hx && py >= 0 && py <= top
          : axis === 'x' ? Math.abs(pz) <= hz && py >= 0 && py <= top
          : Math.abs(px) <= hx && Math.abs(pz) <= hz;
        if (inside) candidates.push({ face, t });
      };
      cross('front', 'z', -hz);
      cross('back', 'z', hz);
      cross('left', 'x', -hx);
      cross('right', 'x', hx);
      // The flat ceiling — or, under a profile, each plane hit within its own rectangle.
      const planes = boardLayoutsRef.current?.ceilingPlanes ?? [];
      if (planes.length === 0) cross('ceiling', 'y', height);
      for (const p of planes) {
        if (!wallGroupsRef.current[p.id]) continue;
        const origin = planePointToWorld(p, 0, 0);
        const n = new THREE.Vector3(...p.plane.normal);
        const denom = n.dot(d);
        if (Math.abs(denom) < 1e-9) continue;
        const t = n.dot(origin.clone().sub(o)) / denom;
        if (t <= 0) continue;
        const hit = o.clone().addScaledVector(d, t).sub(origin);
        const sx = hit.dot(new THREE.Vector3(...p.plane.u));
        const sy = hit.dot(new THREE.Vector3(...p.plane.v));
        if (sx >= 0 && sx <= p.plane.width && sy >= 0 && sy <= p.plane.height) candidates.push({ face: p.id, t });
      }
      candidates.sort((a, b) => a.t - b.t);
      const solid = candidates.find((c) => shown[isCeilingFace(c.face) ? 'ceiling' : (c.face as WallFace)] >= 0.5);
      return solid ?? null;
    };

    // Floor plans: the nearest thing of ANOTHER room the ray meets before
    // `within` — a wall you can see, or its floor. The focus room's own floor
    // stops the ray too (what is under it is not what was clicked).
    const pickOtherRoom = (within: number): string | null => {
      const { origin: o, direction: d } = raycaster.ray;
      let nearest = within;
      let found: string | null = null;
      const floorT = d.y < -1e-9 ? -o.y / d.y : Infinity;
      const fx = o.x + d.x * floorT;
      const fz = o.z + d.z * floorT;
      if (Number.isFinite(floorT) && Math.abs(fx) <= length / 2 && Math.abs(fz) <= width / 2) nearest = Math.min(nearest, floorT);
      for (const room of contextBuiltRef.current) {
        for (const face of WALL_FACES) {
          if (room.opacity[face] < 0.5) continue;
          const [a, b] = room.segments[face];
          const alongX = Math.abs(a.z - b.z) < 1e-9; // front / back walls run along x
          const dn = alongX ? d.z : d.x;
          if (Math.abs(dn) < 1e-9) continue;
          const t = ((alongX ? a.z - o.z : a.x - o.x)) / dn;
          if (t <= 0 || t >= nearest) continue;
          const along = alongX ? o.x + d.x * t : o.z + d.z * t;
          const y = o.y + d.y * t;
          const lo = alongX ? Math.min(a.x, b.x) : Math.min(a.z, b.z);
          const hi = alongX ? Math.max(a.x, b.x) : Math.max(a.z, b.z);
          if (along >= lo && along <= hi && y >= 0 && y <= room.height) { nearest = t; found = room.id; }
        }
        if (Number.isFinite(floorT) && floorT > 0 && floorT < nearest) {
          const s = room.segments;
          if (fx >= s.left[0].x && fx <= s.right[0].x && fz >= s.front[0].z && fz <= s.back[0].z) { nearest = floorT; found = room.id; }
        }
      }
      return found;
    };

    const onClick = (e: MouseEvent) => {
      if (dragMovedRef.current) return; // the end of an orbit drag
      if (isAnimatingRef.current) return;
      if (Date.now() - touchDragEndedAtRef.current < 400) return; // the end of a touch drag
      const cam = cameraRef.current;
      const canvas = rendererRef.current?.domElement;
      if (!cam || !canvas || e.target !== canvas) return;

      const rect = container.getBoundingClientRect();
      const ndc = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(ndc, cam);

      // A door's handle (and later a window's, a garage door's button): open or
      // close it — unless a wall of this room stands in front of it.
      if (!isLockedRef.current && openerHandlesRef.current.length > 0) {
        const handleHits = raycaster.intersectObjects(openerHandlesRef.current, false);
        if (handleHits.length > 0) {
          const wall = pickFace();
          if (!wall || wall.t >= handleHits[0].distance - 0.3) {
            let o: THREE.Object3D | null = handleHits[0].object;
            while (o && typeof o.userData.openerId !== 'string') o = o.parent;
            if (o) {
              toggleOpenerRef.current(o.userData.openerId);
              return;
            }
          }
        }
      }

      const lockedFace = selectedFaceRef.current;
      if (!isLockedRef.current || !lockedFace) {
        const hit = pickFace();
        const other = onRoomFocus && !isInsideRoomRef.current ? pickOtherRoom(hit ? hit.t : Infinity) : null;
        if (other) {
          // Held back a moment: this may be the first click of a double-click
          // on that room's floor (which opens the planner instead).
          if (focusClickTimerRef.current !== null) window.clearTimeout(focusClickTimerRef.current);
          focusClickTimerRef.current = window.setTimeout(() => {
            focusClickTimerRef.current = null;
            onRoomFocus?.(other);
          }, FOCUS_CLICK_DELAY_MS);
        } else if (hit) {
          // From inside, a click that goes through a hole in the wall (a wall taken
          // out between two rooms) is not a click on that wall — it may be the
          // first click of a double-click on the next room's floor.
          // Anywhere else on the wall — panelled or bare studs — locks it.
          if (isInsideRoomRef.current && !isCeilingFace(hit.face)) {
            const world = raycaster.ray.origin.clone().addScaledVector(raycaster.ray.direction, hit.t);
            const p = roomGroupRef.current ? roomGroupRef.current.worldToLocal(world) : world;
            const along = hit.face === 'front' ? p.x : hit.face === 'back' ? length - p.x : hit.face === 'left' ? p.z : width - p.z;
            const wall = boardLayoutsRef.current?.walls[WALL_FACES.indexOf(hit.face as WallFace)];
            const through = (wall?.openings ?? []).some((o) => {
              if (along < o.x || along > o.x + o.width || p.y < o.y || p.y > o.y + o.height) return false;
              if (o.kind === 'passage') return true;
              // A door or garage door standing open is a way through too.
              if ((o.kind === 'door' || o.kind === 'garageDoor') && o.key) {
                const id = o.key.startsWith('link:') ? o.key : `${roomKey ?? 'room'}|${o.key}`;
                return (openersRef.current.get(id)?.target ?? 0) > 0.5;
              }
              return false;
            });
            if (through) return;
          }
          lockToFace(hit.face);
        }
        return;
      }

      // Locked on a ceiling plane: a click on ANOTHER plane moves the lock there.
      if (planeFor(lockedFace)) {
        const { origin: o, direction: d } = raycaster.ray;
        let nearest: { face: SurfaceFace; t: number } | null = null;
        for (const p of boardLayoutsRef.current?.ceilingPlanes ?? []) {
          const origin = planePointToWorld(p, 0, 0);
          const n = new THREE.Vector3(...p.plane.normal);
          const denom = n.dot(d);
          if (Math.abs(denom) < 1e-9) continue;
          const t = n.dot(origin.clone().sub(o)) / denom;
          if (t <= 0) continue;
          const hit = o.clone().addScaledVector(d, t).sub(origin);
          const sx = hit.dot(new THREE.Vector3(...p.plane.u));
          const sy = hit.dot(new THREE.Vector3(...p.plane.v));
          if (sx >= 0 && sx <= p.plane.width && sy >= 0 && sy <= p.plane.height && (!nearest || t < nearest.t)) nearest = { face: p.id, t };
        }
        if (nearest && nearest.face !== lockedFace) { lockToFace(nearest.face); return; }
      }

      const group = wallGroupsRef.current[lockedFace];
      if (!group) return;
      const hits = raycaster.intersectObject(group, true);

      // Mesh-based pick: each section's meshes carry userData.sectionId, so the
      // first hit that has one (itself or an ancestor) is the section. Robust to
      // the extruded geometry's depth, T&G overlap and groove recesses.
      if (onSectionPick) {
        for (const hit of hits) {
          let obj: THREE.Object3D | null = hit.object;
          while (obj) {
            // An accessory the user hung is picked like a section (its id selects it).
            const aid = obj.userData?.accessoryId;
            if (typeof aid === 'string') { onSectionPick(aid); return; }
            const sid = obj.userData?.sectionId;
            if (typeof sid === 'string') { onSectionPick(sid); return; }
            if (obj === group) break; // don't climb past the wall/ceiling group
            obj = obj.parent;
          }
        }
        onSectionPick(null); // bare wall
        return;
      }

      // Fallback (no onSectionPick): report surface-local 2D and let the host map it.
      if (hits.length === 0) return;
      const p = roomGroupRef.current ? roomGroupRef.current.worldToLocal(hits[0].point.clone()) : hits[0].point;
      let lx: number;
      let ly: number;
      const lockedPlane = planeFor(lockedFace);
      if (lockedPlane) {
        const rel = hits[0].point.clone().sub(planePointToWorld(lockedPlane, 0, 0));
        lx = rel.dot(new THREE.Vector3(...lockedPlane.plane.u));
        ly = rel.dot(new THREE.Vector3(...lockedPlane.plane.v));
      } else {
        switch (lockedFace as WallFace | 'ceiling') {
          case 'front': lx = p.x; ly = p.y; break;
          case 'back': lx = length - p.x; ly = p.y; break;
          case 'left': lx = p.z; ly = p.y; break;
          case 'right': lx = width - p.z; ly = p.y; break;
          case 'ceiling': lx = p.x; ly = p.z; break;
          default: return;
        }
      }
      onSurfacePick?.(lockedFace, lx, ly);
    };

    // Over a handle the pointer becomes a hand: it can be clicked.
    const onHover = (e: PointerEvent) => {
      const cam = cameraRef.current;
      const canvas = rendererRef.current?.domElement;
      if (!cam || !canvas || e.buttons !== 0) return;
      let over = false;
      if (!isLockedRef.current && openerHandlesRef.current.length > 0 && e.target === canvas) {
        const rect = container.getBoundingClientRect();
        raycaster.setFromCamera(new THREE.Vector2(((e.clientX - rect.left) / rect.width) * 2 - 1, -((e.clientY - rect.top) / rect.height) * 2 + 1), cam);
        over = raycaster.intersectObjects(openerHandlesRef.current, false).length > 0;
      }
      const want = over ? 'pointer' : '';
      if (canvas.style.cursor !== want && (over || canvas.style.cursor === 'pointer')) canvas.style.cursor = want;
    };

    container.addEventListener('click', onClick);
    container.addEventListener('pointermove', onHover);
    return () => {
      container.removeEventListener('click', onClick);
      container.removeEventListener('pointermove', onHover);
    };
  }, [editable, lockToFace, onSurfacePick, onSectionPick, onRoomFocus, length, width, height, roomKey, planeFor, planePointToWorld, wallTopAt]);

  // Open or close a door: ease every copy of it (both sides of a shared wall)
  // toward where it is going, a frame at a time.
  const toggleOpenerRef = useRef<(key: string) => void>(() => {});
  toggleOpenerRef.current = (key: string) => {
    const state = openersRef.current.get(key) ?? { current: 0, target: 0 };
    state.target = state.target > 0.5 ? 0 : 1;
    openersRef.current.set(key, state);
    if (openerFrameRef.current !== null) return;
    let last = performance.now();
    const tick = () => {
      const now = performance.now();
      const step = (now - last) / OPENER_MS;
      last = now;
      let moving = false;
      openersRef.current.forEach((s) => {
        if (s.current === s.target) return;
        s.current = s.target > s.current ? Math.min(s.target, s.current + step) : Math.max(s.target, s.current - step);
        moving = true;
      });
      for (const { part, key: k } of movingPartsRef.current) placeMovingPart(part, easeInOutCubic(openersRef.current.get(k)?.current ?? 0));
      // A door on the move: its shadow moves with it.
      if (rendererRef.current) rendererRef.current.shadowMap.needsUpdate = true;
      pipelineRef.current?.invalidateShadows();
      renderSceneRef.current();
      openerFrameRef.current = moving ? requestAnimationFrame(tick) : null;
    };
    openerFrameRef.current = requestAnimationFrame(tick);
  };
  useEffect(() => () => { if (openerFrameRef.current !== null) cancelAnimationFrame(openerFrameRef.current); }, []);

  // Re-render once the W&CB bump map finishes loading (viewer renders on-demand).
  useEffect(() => {
    const onBump = () => renderScene();
    window.addEventListener('wcb-bump-loaded', onBump);
    return () => window.removeEventListener('wcb-bump-loaded', onBump);
  }, []);

  // Fullscreen toggle. iPhone Safari has no Element.requestFullscreen — the
  // guarded helpers never call what isn't there, and the button is hidden.
  const canFullscreen = fullscreenSupported();
  const toggleFullscreen = useCallback(() => {
    if (!viewerWrapperRef.current) return;
    if (!document.fullscreenElement) {
      void requestFullscreenSafe(viewerWrapperRef.current).then((ok) => { if (ok) setIsFullscreen(true); });
    } else {
      void exitFullscreenSafe().then((ok) => { if (ok) setIsFullscreen(false); });
    }
  }, []);

  // Listen for fullscreen changes — resize renderer to match new container size
  useEffect(() => {
    const handler = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      // Delay resize to let the DOM settle after fullscreen transition
      setTimeout(() => syncSizeRef.current(), 100);
    };
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const resetView = () => {
    if (isAnimating || !cameraRef.current) return;
    // resetView only makes sense outside edit mode (the ortho camera doesn't
    // have rotation/fov to animate). The button is hidden during lock.
    if ((cameraRef.current as THREE.OrthographicCamera).isOrthographicCamera) return;
    setIsAnimating(true);
    const persp = cameraRef.current as THREE.PerspectiveCamera;
    const start = { rotation: { ...rotationRef.current }, fov: persp.fov };
    // Inside room: reset to looking straight at back wall. Outside: reset to default orbit.
    const targetRotation = isInsideRoomRef.current ? { x: 0, y: 0 } : { ...INITIAL_ROTATION };
    const targetFov = isInsideRoomRef.current ? 75 : DEFAULT_FOV;
    const target = { rotation: targetRotation, fov: targetFov };
    // And back to the room's centre, if the eye had been moved — and to the
    // default eye height, if it had been raised or lowered.
    const startPivot = pivotOffsetRef.current.clone();
    const startLift = eyeLiftRef.current;
    const duration = 800;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const state = interpolateState(start, target, progress);
      rotationRef.current = state.rotation;
      pivotOffsetRef.current.copy(startPivot).multiplyScalar(1 - easeInOutCubic(progress));
      eyeLiftRef.current = startLift * (1 - easeInOutCubic(progress));
      if (cameraRef.current && !((cameraRef.current as THREE.OrthographicCamera).isOrthographicCamera)) {
        const persp = cameraRef.current as THREE.PerspectiveCamera;
        persp.fov = state.fov;
        persp.updateProjectionMatrix();
      }
      renderScene();
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
        setHasRotated(false);
        animationFrameRef.current = null;
      }
    };
    animate();
  };

  // Pointer travel (px) before a press becomes an orbit drag. Only in edit mode,
  // where a press that doesn't travel is a click that locks a surface; the
  // wizard's viewers rotate from the first pixel, as they always have.
  const DRAG_THRESHOLD_PX = editable ? 4 : 0;

  const handleMouseDown = (e: React.MouseEvent) => {
    pointerDownRef.current = { x: e.clientX, y: e.clientY };
    dragMovedRef.current = false;
    if (isLockedRef.current) return; // orbit disabled while a wall is locked
    isDraggingRef.current = true;
    setIsDragging(true);
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDraggingRef.current || isLockedRef.current) return;
    if (!dragMovedRef.current) {
      const travel = Math.hypot(e.clientX - pointerDownRef.current.x, e.clientY - pointerDownRef.current.y);
      if (travel < DRAG_THRESHOLD_PX) return;
      dragMovedRef.current = true;
    }
    const deltaX = e.clientX - previousMouseRef.current.x;
    const deltaY = e.clientY - previousMouseRef.current.y;
    rotationRef.current = applyDragRotation(rotationRef.current, deltaX, deltaY);
    previousMouseRef.current = { x: e.clientX, y: e.clientY };
    setHasRotated(true);
    renderScene();
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    setIsDragging(false);
  };

  return (
    <div ref={viewerWrapperRef} className={`room-3d-viewer ${compact ? 'compact' : ''} ${isFullscreen ? 'fullscreen' : ''} ${fillParent ? 'fill' : ''}`}>
      {/* Dimension card — centered on top border of viewer */}
      <div className="viewer-dimension-card" style={editable && selectedFace ? { display: 'none' } : undefined}>
        {showHeight
          ? t('viewer.roomDims', {
              length: formatDimension(length),
              width: formatDimension(width),
              // Under a sloped ceiling: the walls' height to the peak.
              height: (() => { const peak = ceilingPeak(boardLayouts ?? undefined, height); return peak > height + 1e-6 ? `${formatDimension(height)} – ${formatDimension(peak)}` : formatDimension(height); })(),
            })
          : t('viewer.ceilingDims', {
              length: formatDimension(length),
              width: formatDimension(width),
            })
        }
      </div>
      {editable && selectedFace && (
        <button
          type="button"
          onClick={exitEditMode}
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 30,
            padding: '6px 12px', fontSize: 'var(--font-size-xs)', fontWeight: 700,
            background: '#1a1a2e', color: '#fff', border: 'none',
            borderRadius: 'var(--radius-md)', cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          }}
        >
          {S.exitEditMode}
        </button>
      )}
      <div
        ref={containerRef}
        className="viewer-container"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        {/* Loading spinner overlay — shown instantly on rebuild, fades out when ready */}
        <div
          ref={spinnerRef}
          style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: '#f9fafb', zIndex: 2, borderRadius: '8px',
            opacity: sceneReady ? 0 : 1,
            pointerEvents: sceneReady ? 'none' : 'auto',
            transition: sceneReady ? 'opacity 0.5s ease-in-out' : 'none',
          }}
        >
          <div style={{
            width: '32px', height: '32px', border: '3px solid #e5e7eb',
            borderTopColor: '#6b7280', borderRadius: '50%',
            animation: 'viewer-spin 0.8s linear infinite',
          }} />
        </div>
        {/* Editor overlay slot — rendered INSIDE the canvas container so the
            projection's container-relative pixel coords map 1:1 to overlay
            children's coord space (no header/footer offset). */}
        {overlay && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
            {overlay}
          </div>
        )}
      </div>

      <div className="viewer-hint">{editable
        ? (selectedFace
          ? `${S.hintLocked} | ${S.hintZoom}`
          : `${isInsideRoom ? S.hintLookAround : S.hintOrbit} | ${S.hintWheelZoom}${isInsideRoom ? ` | ${S.hintEyeHeight}` : ''} | ${S.hintLock}`)
        : t('viewer.hint')}{isFullscreen ? t('viewer.hintEsc') : ''}</div>
      <div className="viewer-controls">
        <button
          className="btn btn-secondary"
          onClick={resetView}
          disabled={!hasRotated || isAnimating}
          title={t('viewer.resetCamera')}
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
            fontSize: 'var(--font-size-xs)', fontWeight: 600,
            cursor: (hasRotated && !isAnimating) ? 'pointer' : 'not-allowed',
            opacity: (hasRotated && !isAnimating) ? 1 : 0.5, transition: 'all 0.2s ease',
          }}
        >
          <RotateCcw size={14} />
          {t('viewer.resetView')}
        </button>
        {/* Stand in the middle of the room and look around. Not while a surface
            is locked for editing (that is a head-on view from outside). */}
        {showHeight && showBoards && !(editable && selectedFace) && (
          <button
            className="btn btn-secondary"
            onClick={toggleInsideRoom}
            disabled={isAnimating}
            title={isInsideRoom ? t('viewer.exitRoomTitle') : t('viewer.enterRoomTitle')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600,
              cursor: !isAnimating ? 'pointer' : 'not-allowed',
              opacity: !isAnimating ? 1 : 0.5, transition: 'all 0.2s ease',
            }}
          >
            {isInsideRoom ? <DoorClosed size={14} /> : <DoorOpen size={14} />}
            {isInsideRoom ? t('viewer.exitRoom') : t('viewer.enterRoom')}
          </button>
        )}
        {showHeight && (
          <button
            className="btn btn-secondary"
            onClick={() => setShowFraming((v) => !v)}
            title={showFraming ? S.framingHideTitle : S.framingShowTitle}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <Frame size={14} />
            {showFraming ? S.framingHide : S.framingShow}
          </button>
        )}
        {canFullscreen && (
          <button
            className="btn btn-secondary"
            onClick={toggleFullscreen}
            title={isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px',
              fontSize: 'var(--font-size-xs)', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            {isFullscreen ? <Minimize size={14} /> : <Maximize size={14} />}
            {isFullscreen ? t('viewer.exitFullScreen') : t('viewer.fullScreen')}
          </button>
        )}
      </div>
    </div>
  );
};

export default Room3DViewer;
